import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Calendar, MapPin, Clock, Users, ChevronLeft, ChevronRight, X, Package, AlertTriangle, Download, Phone, ExternalLink, FileText, Globe } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useVistaEmpleado } from '../context/VistaEmpleadoContext';
import { getAuthToken } from '../api/api';
import { toast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { revisarNovedades } from '../components/guia/novedades';
import { fechaLocalISO, lunesDeLaSemana } from '../utils/pantallasUnDia';
import {
  LETRAS_DIA, NOMBRES_DIA, NOMBRES_MES, COLORES_MOVIL,
  sumarDias, diasDeLaSemana, semanasDelMes, semanaTocaMes, textoRangoSemana,
  diaISO, diaDeEvento, claveEvento, esConfirmado, esJornada, nombreCorto,
  tramoEvento, textoHora, ejeHoras, repartir, barrasMultieventoSemana, estiloEventoMovil,
} from '../utils/calendarioMovil';

// Vistas del móvil; la última elegida se recuerda en el navegador
const VISTAS_MOVIL = ['mes', 'semana', 'lista'];
const CLAVE_VISTA_MOVIL = 'calendario_vista_movil';
const leerVistaMovil = () => {
  try {
    const v = window.localStorage.getItem(CLAVE_VISTA_MOVIL);
    return VISTAS_MOVIL.includes(v) ? v : 'semana';
  } catch (e) {
    return 'semana';
  }
};
// Alto de una hora en la vista Semana (px)
const PX_HORA = 40;
// Ancho de una columna de día en la vista Semana (gutter de horas 34 px + 4 px a la derecha)
// Semana: cada evento a la vez tiene su propio carril de ANCHO_CARRIL px; un día
// mide sus carriles (los días sin eventos, ANCHO_DIA_VACIO) y la semana entera
// se desplaza en horizontal con las horas fijas a la izquierda
const ANCHO_CARRIL = 72;
const ANCHO_DIA_VACIO = 44;
const ANCHO_HORAS = 34;

import { urlIrAlEvento } from '../utils/logistica2';
import useAvisoHorarios from '../components/presupuesto/AvisoHorarios';
import { lineasJornadaConPlatos } from '../utils/multieventoCabecera';
import {
  cargarDatosPorHacer, tareasPorHacerDeDatos, necesitaCatalogos, cargarCatalogosPorHacer,
} from '../utils/porHacerEvento';

// Ausencias: el servidor solo manda las que el usuario puede ver (dirección
// todas, RRHH todas menos las de médico, el resto todas las suyas). El
// día ocupado (médico, curso, trámite...; las citas médicas antiguas ya llegan
// como día ocupado de médico) va en amarillo, distinto del ámbar del resto,
// con la etiqueta del motivo: "Curso · nombre · 09:00 · lugar"
const ESTILO_DIA_OCUPADO = { backgroundColor: '#FDE68A', color: '#78350F' };
const esDiaOcupado = (a) => a?.tipo === 'dia_ocupado' || a?.tipo === 'cita_medica';
const etiquetaAusencia = (a) => a?.tipo_etiqueta || (esDiaOcupado(a) ? 'Día ocupado' : 'Ausencia');
const primerNombre = (nombre) => String(nombre || '').split(' ')[0];
// Texto corto (celda del mes)
const textoAusenciaCorto = (a) => `${etiquetaAusencia(a)} · ${primerNombre(a.empleado_nombre)}`;
// Texto completo (panel y ventana del día): solo las partes que existan
const textoAusenciaLargo = (a) => (esDiaOcupado(a)
  ? [etiquetaAusencia(a), a.empleado_nombre, a.hora, a.lugar].filter(Boolean).join(' · ')
  : `${etiquetaAusencia(a)} · ${a.empleado_nombre}`);
// Resumen de un día: amarillo si todas son días ocupados, si no el ámbar de siempre
const soloDiasOcupados = (lista) => Array.isArray(lista) && lista.length > 0 && lista.every(esDiaOcupado);

// Lugar de una jornada: el suyo si tiene dirección propia (distinta de la
// principal) o el principal (mismo criterio que multievento_utils.lugar_de_jornada).
// Con coordenadas y place_id del mismo sitio, para "Ir al evento".
const lugarDeJornadaCalendario = (jornada, presupuesto) => {
  const j = jornada || {};
  const p = presupuesto || {};
  const propio = String(j.lugar_evento || j.direccion_exacta || j.direccion_evento || '').trim();
  const principal = String(p.lugar_evento || p.direccion_exacta || p.direccion_evento || '').trim();
  const hereda = !propio || propio.toLowerCase() === principal.toLowerCase();
  const origen = hereda ? p : j;
  return {
    lugar_evento: origen.lugar_evento || origen.direccion_exacta || origen.direccion_evento || '',
    direccion_exacta: origen.direccion_exacta || origen.direccion_evento || '',
    coordenadas: origen.coordenadas || null,
    place_id: origen.place_id || null,
  };
};

const CalendarioEventos = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { usuarioEfectivo, puedeVerRuta } = useVistaEmpleado();
  // M1: servicios sin hora antes de la orden de cocina (aviso, no bloqueo)
  const { revisarHorarios, dialogoHorarios } = useAvisoHorarios();
  // ?fecha=YYYY-MM-DD (desde Mi Inicio): abre ese mes y lleva la vista a ese día
  const [searchParams] = useSearchParams();
  const fechaParam = (() => {
    const f = String(searchParams.get('fecha') || '');
    return /^\d{4}-\d{2}-\d{2}$/.test(f) ? f : '';
  })();
  const [fechaFoco] = useState(fechaParam);
  const focoHecho = useRef(false);
  // ?evento=<id del presupuesto> (desde la ficha, "Ver en el calendario"):
  // con los eventos cargados se abre su ventana en el día de ?fecha= (una vez)
  const [eventoFoco] = useState(() => String(searchParams.get('evento') || ''));
  const eventoFocoHecho = useRef(false);
  const [currentDate, setCurrentDate] = useState(() => {
    if (fechaParam) {
      const [y, m, d] = fechaParam.split('-').map(Number);
      const fecha = new Date(y, m - 1, d);
      if (!Number.isNaN(fecha.getTime())) return fecha;
    }
    return new Date();
  });
  const [presupuestos, setPresupuestos] = useState([]);
  const [ausencias, setAusencias] = useState([]); // vacaciones/permisos aprobados del equipo
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  // Pestaña Dirección de la ventana del evento: el Por hacer de su ficha
  const [porHacerEvento, setPorHacerEvento] = useState({ id: null, cargando: false, tareas: null, error: null });
  const [activeTab, setActiveTab] = useState('detalles');
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  // Móvil: Mes | Semana | Lista (por defecto la semana actual; se recuerda la última)
  const [vistaMovil, setVistaMovil] = useState(leerVistaMovil);
  // Lunes de la semana que se ve en la vista Semana del móvil
  const [semanaFecha, setSemanaFecha] = useState(() => lunesDeLaSemana(currentDate));
  // Panel inferior del móvil: { tipo: 'dia' | 'claves' | 'ausencias', fecha: 'YYYY-MM-DD', claves?, grupo? }
  const [panelMovil, setPanelMovil] = useState(null);
  // Semana móvil: contenedor que se desplaza y posición de hoy para llevarlo a la vista
  const scrollSemanaRef = useRef(null);
  const hoySemanaRef = useRef(null);
  // Filtros por casillas (confirmados, multievento, pendientes, vacaciones y ausencias);
  // se recuerdan por navegador. Sin acceso a localStorage, solo en memoria.
  const FILTROS_POR_DEFECTO = { confirmados: true, multievento: true, pendientes: true, ausencias: true };
  const [filtros, setFiltros] = useState(() => {
    try {
      const guardados = JSON.parse(window.localStorage.getItem('calendario_filtros') || '{}');
      return { ...FILTROS_POR_DEFECTO, ...(guardados && typeof guardados === 'object' ? guardados : {}) };
    } catch (e) {
      return { ...FILTROS_POR_DEFECTO };
    }
  });
  const cambiarFiltro = (clave) => {
    setFiltros(prev => {
      const nuevos = { ...prev, [clave]: !prev[clave] };
      try { window.localStorage.setItem('calendario_filtros', JSON.stringify(nuevos)); } catch (e) { /* solo en memoria */ }
      return nuevos;
    });
  };
  // Un evento pasa el filtro según su tipo: pendiente (no confirmado), multievento o confirmado de un día
  const pasaFiltro = (ev) => {
    if (!ev) return false;
    if (ev.estado !== 'confirmado') return filtros.pendientes;
    if (ev.es_multi_evento || ev.es_jornada_multi) return filtros.multievento;
    return filtros.confirmados;
  };
  const filtrarEventos = (lista) => (Array.isArray(lista) ? lista.filter(pasaFiltro) : []);
  const [dayEventsModal, setDayEventsModal] = useState(null); // Para mostrar todos los eventos de un día
  
  // Estados para modal de minuta
  const [showMinutaModal, setShowMinutaModal] = useState(false);
  const [minutaConfig, setMinutaConfig] = useState({
    menusSeleccionados: [],
    platosOcultos: {},
    ordenPlatos: {},
    mostrarIngles: false
  });
  const [generatingMinuta, setGeneratingMinuta] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';
  const isDirectivo = user && (user.rol === 'admin' || user.rol === 'direccion' || user.role === 'admin' || user.role === 'direccion');
  // Botón "Presupuesto" del detalle: solo para quien puede entrar en Presupuestos
  // (misma regla que el menú: permisos finos y, si no, el rol), con la persona
  // simulada si la hay. Operaciones (Juana, Brandon) no lo ven.
  const puedeVerPresupuestos = !!(puedeVerRuta && puedeVerRuta('/presupuestos', usuarioEfectivo || user));
  // Pestaña Dirección de la ventana del evento: solo dirección, y en "Simular
  // vista empleado" cuenta el rol del empleado simulado (ve lo que vería él)
  const esDireccionEfectiva = !!usuarioEfectivo && ['admin', 'direccion'].includes(usuarioEfectivo.rol || usuarioEfectivo.role);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Con ?fecha=, cuando llegan los eventos se lleva la vista a ese día (una vez)
  useEffect(() => {
    if (!fechaFoco || focoHecho.current || presupuestos.length === 0) return undefined;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-foco-fecha="${fechaFoco}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        focoHecho.current = true;
      }
    }, 150);
    return () => clearTimeout(t);
  }, [fechaFoco, presupuestos, vistaMovil]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPresupuestos();
    }, 500);
    return () => clearTimeout(timer);
  }, [currentDate, isDirectivo]);

  useEffect(() => {
    const checkToken = setInterval(() => {
      const token = getAuthToken();
      if (token && presupuestos.length === 0) {
        fetchPresupuestos();
      }
    }, 1000);
    const clearCheckToken = setTimeout(() => clearInterval(checkToken), 5000);
    return () => {
      clearInterval(checkToken);
      clearTimeout(clearCheckToken);
    };
  }, [presupuestos.length]);


  const fetchPresupuestos = async () => {
    try {
      const token = getAuthToken();
      if (!token) return;
      
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      // U-101 (lote 7): rango con la fecha local (toISOString daba el día
      // anterior en España y dejaba fuera el último día del rango)
      const fechaInicio = fechaLocalISO(new Date(year, month - 1, 1));
      const fechaFin = fechaLocalISO(new Date(year, month + 2, 0));
      
      // Si es directivo, traer también presupuestos no confirmados (borrador)
      const estadoParam = isDirectivo ? '' : 'estado=confirmado&';
      const response = await fetch(
        `${API_URL}/api/presupuestos/calendario?${estadoParam}fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}`, 
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      if (!response.ok) return;
      const data = await response.json();
      let eventos = Array.isArray(data.eventos) ? data.eventos : (data.presupuestos || []);
      // Si es directivo, filtrar: confirmados + borrador (excluir cancelados)
      if (isDirectivo) {
        eventos = eventos.filter(e => e.estado !== 'cancelado');
      }
      setPresupuestos(eventos);
      setAusencias(Array.isArray(data.ausencias) ? data.ausencias : []);
    } catch (error) {
      console.error('Error:', error);
      setPresupuestos([]);
      setAusencias([]);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    let startingDayOfWeek = firstDay.getDay();
    startingDayOfWeek = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;
    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) days.push(new Date(year, month, i));
    return days;
  };

  const getEventsForDay = (day) => {
    if (!day || !Array.isArray(presupuestos)) return [];
    const year = day.getFullYear();
    const month = String(day.getMonth() + 1).padStart(2, '0');
    const dayNum = String(day.getDate()).padStart(2, '0');
    const dayStr = `${year}-${month}-${dayNum}`;
    
    // El backend ya devuelve eventos planos (cada jornada expandida como evento individual)
    return filtrarEventos(presupuestos
      .filter(p => p.fecha_evento?.split('T')[0] === dayStr)
      .map(p => ({
        ...p,
        es_jornada_multi: !!p.es_multi_evento
      })));
  };

  const getAusenciasForDay = (day) => {
    if (!day || !Array.isArray(ausencias)) return [];
    const year = day.getFullYear();
    const month = String(day.getMonth() + 1).padStart(2, '0');
    const dayNum = String(day.getDate()).padStart(2, '0');
    const dayStr = `${year}-${month}-${dayNum}`;
    if (!filtros.ausencias) return [];
    return ausencias.filter(a => a.fecha === dayStr);
  };

  // Ausencias del mes en curso, una entrada por solicitud (para la vista lista)
  const getAusenciasResumenMes = () => {
    if (!Array.isArray(ausencias) || !filtros.ausencias) return [];
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const vistas = new Set();
    return ausencias
      .filter(a => a.fecha?.startsWith(`${year}-${month}`))
      .filter(a => {
        if (vistas.has(a.solicitud_id)) return false;
        vistas.add(a.solicitud_id);
        return true;
      });
  };

  const getEventsForCurrentMonth = () => {
    if (!Array.isArray(presupuestos)) return [];
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    
    // El backend ya devuelve eventos planos
    return presupuestos
      .filter(p => p.fecha_evento?.split('T')[0]?.startsWith(`${year}-${month}`))
      .map(p => ({
        ...p,
        es_jornada_multi: !!p.es_multi_evento
      }))
      .sort((a, b) => new Date(a.fecha_evento) - new Date(b.fecha_evento));
  };

  const [loadingEvent, setLoadingEvent] = useState(false);
  const isPendingEvent = selectedEvent && selectedEvent.estado !== 'confirmado';

  // Anterior y Siguiente en la ventana del evento: los eventos del mismo día
  // que el abierto (con los filtros), ordenados, y la posición del abierto.
  // Abierto desde fuera de las listas del calendario: null y no hay botones.
  const navEventosRef = useRef(null);
  // Cada apertura lleva su número: la respuesta de un evento que ya no está
  // abierto (se pasó al siguiente antes de que cargara) no pisa al nuevo
  const peticionEventoRef = useRef(0);

  // Orden de la navegación: hora de inicio (sin hora al final) y número
  const ordenarEventosNav = (lista) => {
    const hora = (ev) => {
      const h = String(ev?.hora_inicio || '').trim();
      return /^\d{1,2}:\d{2}/.test(h) ? h.padStart(5, '0') : '99:99';
    };
    return [...lista].sort((a, b) => (
      diaDeEvento(a).localeCompare(diaDeEvento(b))
      || hora(a).localeCompare(hora(b))
      || String(a?.numero ?? '').localeCompare(String(b?.numero ?? ''), 'es', { numeric: true })
      || claveEvento(a).localeCompare(claveEvento(b))
    ));
  };

  // Abre un evento desde una lista del calendario y guarda los de su mismo día
  // (con los filtros y las jornadas de multievento) para Anterior y Siguiente
  const abrirEventoDesde = (evento) => {
    const dia = diaDeEvento(evento);
    const lista = /^\d{4}-\d{2}-\d{2}$/.test(dia) ? ordenarEventosNav(getEventsForDay(fechaDeISO(dia))) : [];
    const indice = lista.findIndex(e => claveEvento(e) === claveEvento(evento));
    navEventosRef.current = indice >= 0 ? { lista, indice } : null;
    handleEventClick(evento, { conContexto: true });
  };
  const abrirEventoDesdeRef = useRef(abrirEventoDesde);
  abrirEventoDesdeRef.current = abrirEventoDesde;

  // Con ?evento=, cuando llegan los eventos se abre el de ese id en el día de
  // ?fecha= (una sola vez) y se quita el parámetro de la dirección para que al
  // cerrar la ventana no vuelva a abrirse. Si no está, queda el día enfocado.
  useEffect(() => {
    if (!eventoFoco || eventoFocoHecho.current || presupuestos.length === 0) return;
    eventoFocoHecho.current = true;
    const evento = presupuestos.find(e => (
      (e.id === eventoFoco || e.presupuesto_id === eventoFoco)
      && (!fechaFoco || diaDeEvento(e) === fechaFoco)
    ));
    if (evento) abrirEventoDesdeRef.current({ ...evento, es_jornada_multi: !!evento.es_multi_evento });
    const params = new URLSearchParams(searchParams);
    params.delete('evento');
    const busqueda = params.toString();
    navigate({ search: busqueda ? `?${busqueda}` : '' }, { replace: true });
  }, [eventoFoco, fechaFoco, presupuestos, searchParams, navigate]);

  // Pestaña al abrir o pasar de evento: la misma si el nuevo también la tiene.
  // En el ordenador los detalles van siempre a la izquierda: a la derecha,
  // Menú si lo tiene y si no Dirección (Detalles solo si no hay ninguna).
  const pestanaParaEvento = (pestana, pendiente) => {
    if (pestana === 'menu' && !pendiente) return 'menu';
    if (pestana === 'direccion' && esDireccionEfectiva) return 'direccion';
    if (!isMobile) {
      if (!pendiente) return 'menu';
      if (esDireccionEfectiva) return 'direccion';
    }
    return 'detalles';
  };

  const handleEventClick = async (presupuesto, opciones = {}) => {
    const isPending = presupuesto.estado !== 'confirmado';
    // Abierto sin lista (desde fuera de las vistas): sin Anterior ni Siguiente
    if (!opciones.conContexto) navEventosRef.current = null;
    peticionEventoRef.current += 1;
    const peticion = peticionEventoRef.current;
    const vigente = () => peticion === peticionEventoRef.current;
    const pestanaInicial = pestanaParaEvento(opciones.pestana, isPending);
    
    // Para presupuestos pendientes, modal simplificado
    if (isPending) {
      setSelectedEvent({
        ...presupuesto,
        cliente: { nombre_empresa: presupuesto.cliente_nombre || 'Cliente' },
        menus: []
      });
      setShowModal(true);
      setActiveTab(pestanaInicial);
      setLoadingEvent(false);
      revisarNovedades();
      return;
    }
    
    // Mostrar modal inmediatamente con datos básicos
    setSelectedEvent({
      ...presupuesto,
      cliente: { nombre_empresa: presupuesto.cliente_nombre || 'Cargando...' },
      contacto_evento: null,
      menus: []
    });
    setShowModal(true);
    setActiveTab(pestanaInicial);
    setLoadingEvent(true);
    revisarNovedades();

    try {
      const token = getAuthToken();
      
      // Fetch presupuesto completo y cliente en paralelo
      const [presupuestoRes, clienteRes] = await Promise.all([
        fetch(`${API_URL}/api/presupuestos/${presupuesto.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        presupuesto.cliente_id ? fetch(`${API_URL}/api/clientes/${presupuesto.cliente_id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).catch(() => null) : Promise.resolve(null)
      ]);

      const presupuestoData = await presupuestoRes.json();
      const clienteData = clienteRes?.ok ? await clienteRes.json() : {};
      if (!vigente()) return;
      
      // Determinar contacto
      let contactoData = null;
      if (presupuestoData.persona_contacto_id === 'principal') {
        contactoData = {
          nombre: clienteData.persona_contacto || 'No especificado',
          telefono: clienteData.telefono_principal || clienteData.telefono || ''
        };
      } else if (presupuestoData.persona_contacto_id) {
        try {
          const contactoRes = await fetch(`${API_URL}/api/personas-contacto/${presupuestoData.persona_contacto_id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (contactoRes.ok) contactoData = await contactoRes.json();
        } catch (err) { /* ignorar */ }
        if (!vigente()) return;
      } else {
        contactoData = {
          nombre: clienteData.persona_contacto || 'No especificado',
          telefono: clienteData.telefono_principal || clienteData.telefono || ''
        };
      }
      
      // Entrada de una jornada (multievento): sus datos (fecha, horas,
      // personas, lugar, observaciones) mandan sobre los de la cabecera, que
      // son los de la primera jornada
      const jornadaData = presupuesto.jornada_id
        ? (presupuestoData.jornadas || []).find(j => j && j.id === presupuesto.jornada_id) || null
        : null;
      const camposJornada = presupuesto.jornada_id ? {
        id: presupuesto.id,
        presupuesto_id: presupuesto.presupuesto_id || presupuesto.id,
        jornada_id: presupuesto.jornada_id,
        jornada_numero: presupuesto.jornada_numero,
        total_jornadas: presupuesto.total_jornadas || (presupuestoData.jornadas || []).length,
        es_jornada: true,
        fecha_evento: presupuesto.fecha_evento,
        hora_inicio: presupuesto.hora_inicio,
        hora_fin: presupuesto.hora_fin,
        num_personas: jornadaData?.num_personas ?? presupuesto.num_personas,
        observaciones: [presupuestoData.observaciones, jornadaData?.observaciones ?? presupuesto.observaciones]
          .map(t => String(t || '').trim()).filter(Boolean).join('\n'),
        ...lugarDeJornadaCalendario(jornadaData || presupuesto, presupuestoData),
      } : {};

      // Actualizar con datos básicos inmediatamente
      setSelectedEvent(prev => ({
        ...prev,
        ...presupuestoData,
        ...camposJornada,
        cliente: clienteData,
        contacto_evento: contactoData
      }));

      // Cargar menús en paralelo (en segundo plano). En una jornada, sus
      // líneas (con los platos de su línea de la cabecera si no tiene)
      const menusPersonalizados = jornadaData
        ? lineasJornadaConPlatos(jornadaData, presupuestoData.menus_personalizados || [])
        : (presupuestoData.menus_personalizados || []);
      if (menusPersonalizados.length > 0) {
        const menuPromises = menusPersonalizados.map(async (menuPers) => {
          const menuId = menuPers.menu_id;
          // Build base menu info from presupuesto data (always available)
          const baseMenu = {
            menu_id: menuId,
            nombre: menuPers.nombre_menu || 'Menu personalizado',
            num_personas: menuPers.num_personas,
            precio_unitario: menuPers.precio_unitario || menuPers.precio_ud,
            bebida_incluida: menuPers.bebida_incluida,
            platos: []
          };
          if (!menuId) return baseMenu;

          // For custom menus, skip catalog fetch (they don't exist there)
          let menuData = null;
          const isCustom = menuId.startsWith('custom-');
          if (!isCustom) {
            try {
              const menuRes = await fetch(`${API_URL}/api/menus/${menuId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              if (menuRes.ok) {
                menuData = await menuRes.json();
              }
            } catch (e) { /* catalog fetch failed, use presupuesto data */ }
          }

          // Use platos_ids from presupuesto first, then from catalog menu
          const platosIds = menuPers.platos_ids?.length > 0
            ? menuPers.platos_ids
            : (menuData?.platos_ids || []);

          // Fetch dish details
          let platos = [];
          if (platosIds.length > 0) {
            try {
              const platosResults = await Promise.all(
                platosIds.map(platoId =>
                  fetch(`${API_URL}/api/platos/${platoId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                  }).then(r => r.ok ? r.json() : null).catch(() => null)
                )
              );
              platos = platosResults.filter(Boolean);
            } catch (e) { /* platos fetch failed, show menu without dishes */ }
          }

          return {
            ...(menuData || {}),
            menu_id: menuId,
            nombre: menuPers.nombre_menu || menuData?.nombre || 'Menu personalizado',
            num_personas: menuPers.num_personas,
            precio_unitario: menuPers.precio_unitario || menuPers.precio_ud || menuData?.precio_persona,
            bebida_incluida: menuPers.bebida_incluida ?? menuData?.bebida_incluida,
            platos
          };
        });

        const menus = await Promise.all(menuPromises);
        if (!vigente()) return;
        setSelectedEvent(prev => ({ ...prev, menus, ...(jornadaData ? { menus_personalizados: menusPersonalizados } : {}) }));
      }
      
    } catch (error) {
      console.error('Error loading event:', error);
    } finally {
      if (vigente()) setLoadingEvent(false);
    }
  };

  // Pasa al evento anterior (-1) o siguiente (+1) de la lista de donde se abrió
  const irAEventoVecino = (delta) => {
    const nav = navEventosRef.current;
    if (!nav || loadingEvent) return;
    const nuevo = nav.indice + delta;
    if (nuevo < 0 || nuevo >= nav.lista.length) return;
    navEventosRef.current = { lista: nav.lista, indice: nuevo };
    handleEventClick(nav.lista[nuevo], { conContexto: true, pestana: activeTab });
  };
  // Siempre la versión del último render, para el atajo de teclado
  const irAEventoVecinoRef = useRef(irAEventoVecino);
  irAEventoVecinoRef.current = irAEventoVecino;

  // Ordenador: Detalles ya está a la izquierda; si la ventana estaba en Detalles
  // (p. ej. al ensanchar la pantalla), la derecha pasa a Menú o Dirección
  useEffect(() => {
    if (isMobile || !showModal || activeTab !== 'detalles') return;
    if (!isPendingEvent) setActiveTab('menu');
    else if (esDireccionEfectiva) setActiveTab('direccion');
  }, [isMobile, showModal, activeTab, isPendingEvent, esDireccionEfectiva]);

  // Escritorio: flechas izquierda y derecha del teclado con la ventana abierta
  // (no si se escribe en un campo ni con la ventana de la minuta encima)
  useEffect(() => {
    if (!showModal || isMobile || showMinutaModal) return undefined;
    const alPulsar = (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey || e.defaultPrevented) return;
      const el = e.target;
      const tag = String(el?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || el?.isContentEditable) return;
      if (!navEventosRef.current) return;
      e.preventDefault();
      irAEventoVecinoRef.current(e.key === 'ArrowLeft' ? -1 : 1);
    };
    window.addEventListener('keydown', alPulsar);
    return () => window.removeEventListener('keydown', alPulsar);
  }, [showModal, isMobile, showMinutaModal]);

  // Descarga un blob renombrando a .html cuando el backend cayó en fallback
  // (WeasyPrint no disponible). Avisa al usuario en ese caso.
  const descargarBlobConFallback = (response, blob, baseName) => {
    const contentType = (response.headers.get('content-type') || '').toLowerCase();
    const esHtml = contentType.includes('text/html');
    const extension = esHtml ? 'html' : 'pdf';
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${baseName}.${extension}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Safari en iOS lee el blob después del clic: se libera pasado un rato
    setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    if (esHtml) {
      toast({
        title: 'El PDF ha salido como HTML',
        description: 'El servidor no pudo generar el PDF y se ha descargado con extensión .html. Si se repite, avisa a dirección.',
        variant: 'destructive',
      });
    }
  };

  const handleDownloadOrdenCocina = async () => {
    const decisionHoras = await revisarHorarios(selectedEvent?.id, {
      accion: 'orden', jornadaId: selectedEvent?.jornada_id || null,
    });
    if (decisionHoras === 'poner') {
      navigate(`/presupuestos/${selectedEvent.id}?pestana=logistica&nivel=horario`);
      return;
    }
    if (decisionHoras !== 'seguir') return;
    try {
      setDownloadingPdf(true);
      const token = getAuthToken();
      // Si es una jornada de multi-evento, pasar el jornada_id
      const jornadaParam = selectedEvent.jornada_id ? `?jornada_id=${selectedEvent.jornada_id}` : '';
      const response = await fetch(`${API_URL}/api/presupuestos/${selectedEvent.id}/generar-orden-cocina${jornadaParam}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        let motivo = '';
        try { motivo = (await response.json())?.detail || ''; } catch (e) { motivo = ''; }
        throw new Error(motivo || 'Error al generar orden de cocina');
      }
      const blob = await response.blob();
      const suffix = selectedEvent.jornada_id ? `_jornada${selectedEvent.jornada_numero}` : '';
      const baseName = `orden_cocina_${selectedEvent.numero || selectedEvent.id?.substring(0, 8)}${suffix}`;
      descargarBlobConFallback(response, blob, baseName);
    } catch (error) {
      console.error('Error:', error);
      toast({ title: 'No se pudo descargar la orden de cocina', description: error?.message && error.message !== 'Failed to fetch' ? error.message : 'Inténtalo de nuevo.', variant: 'destructive' });
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleDownloadHojaMaterial = async () => {
    try {
      setDownloadingPdf(true);
      const token = getAuthToken();
      // Si es una jornada de multi-evento, pasar el jornada_id
      const jornadaParam = selectedEvent.jornada_id ? `?jornada_id=${selectedEvent.jornada_id}` : '';
      const response = await fetch(`${API_URL}/api/presupuestos/${selectedEvent.id}/generar-hoja-material${jornadaParam}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        let motivo = '';
        try { motivo = (await response.json())?.detail || ''; } catch (e) { motivo = ''; }
        throw new Error(motivo || 'Error al generar hoja de material');
      }
      const blob = await response.blob();
      const suffix = selectedEvent.jornada_id ? `_jornada${selectedEvent.jornada_numero}` : '';
      const baseName = `hoja_material_${selectedEvent.numero || selectedEvent.id?.substring(0, 8)}${suffix}`;
      descargarBlobConFallback(response, blob, baseName);
    } catch (error) {
      console.error('Error:', error);
      toast({ title: 'No se pudo descargar la hoja de material', description: error?.message && error.message !== 'Failed to fetch' ? error.message : 'Inténtalo de nuevo.', variant: 'destructive' });
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Abrir modal de configuración de minuta
  const handleOpenMinutaModal = () => {
    // Inicializar con los menús del evento
    const menus = selectedEvent?.menus || selectedEvent?.menus_personalizados || [];
    const menusIds = menus.map((m, idx) => m.menu_id || `menu-${idx}`);
    
    setMinutaConfig({
      menusSeleccionados: menusIds,
      platosOcultos: {},
      ordenPlatos: {},
      mostrarIngles: false
    });
    setShowMinutaModal(true);
  };

  // Generar y descargar minuta
  const handleGenerarMinuta = async () => {
    if (minutaConfig.menusSeleccionados.length === 0) {
      toast({ title: 'Selecciona al menos un menú', description: 'Hace falta un menú para generar la minuta.' });
      return;
    }

    try {
      setGeneratingMinuta(true);
      const token = getAuthToken();
      
      // Multievento: la minuta de ESTA jornada (su fecha y sus platos)
      const jornadaParam = selectedEvent.jornada_id ? `?jornada_id=${selectedEvent.jornada_id}` : '';
      const response = await fetch(`${API_URL}/api/presupuestos/${selectedEvent.id}/generar-minuta${jornadaParam}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(minutaConfig)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Error al generar la minuta');
      }

      const blob = await response.blob();
      const sufijoMinuta = selectedEvent.jornada_id ? `_jornada${selectedEvent.jornada_numero}` : '';
      const baseName = `Minuta_${selectedEvent.numero || selectedEvent.id?.substring(0, 8)}${sufijoMinuta}`;
      descargarBlobConFallback(response, blob, baseName);

      setShowMinutaModal(false);
    } catch (error) {
      console.error('Error generando minuta:', error);
      toast({ title: 'No se pudo generar la minuta', description: error.message, variant: 'destructive' });
    } finally {
      setGeneratingMinuta(false);
    }
  };

  const prevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  const nextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));

  const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  const dayNames = isMobile ? ['L', 'M', 'X', 'J', 'V', 'S', 'D'] : ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
  const days = getDaysInMonth(currentDate);
  const monthEvents = filtrarEventos(getEventsForCurrentMonth());

  // Primer evento del mes en el día de ?fecha= o después (la lista lleva la vista ahí)
  const claveFocoLista = (() => {
    if (!fechaFoco) return null;
    const ev = monthEvents.find(e => (e.fecha_evento || '').split('T')[0] >= fechaFoco);
    return ev ? ev.id + (ev.jornada_id || '') + ev.fecha_evento : null;
  })();
  const isoDia = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  // ---------------------------------------------------------------------------
  // Vista móvil: Mes | Semana | Lista
  // ---------------------------------------------------------------------------
  const guardarVistaMovil = (vista) => {
    setVistaMovil(vista);
    setPanelMovil(null);
    try { window.localStorage.setItem(CLAVE_VISTA_MOVIL, vista); } catch (e) { /* solo en memoria */ }
  };

  // Lleva la vista Semana a la semana de `fecha`; si esa semana no toca el mes
  // cargado, cambia de mes (y la página recarga los datos de ese mes)
  const ponerSemana = (fecha) => {
    const lunes = lunesDeLaSemana(fecha);
    const dias = diasDeLaSemana(lunes);
    setSemanaFecha(lunes);
    setPanelMovil(null);
    if (!semanaTocaMes(dias, currentDate)) {
      setCurrentDate(new Date(dias[3].getFullYear(), dias[3].getMonth(), 1));
    }
  };

  const elegirVistaMovil = (vista) => {
    if (vista === 'semana' && vistaMovil !== 'semana') {
      // La semana que ya se veía si es del mes en pantalla; si no, la de hoy
      // si hoy es de ese mes; si no, la primera semana del mes
      const hoy = new Date();
      let ref = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      if (semanaTocaMes(diasDeLaSemana(semanaFecha), currentDate)) ref = semanaFecha;
      else if (hoy.getFullYear() === currentDate.getFullYear() && hoy.getMonth() === currentDate.getMonth()) ref = hoy;
      ponerSemana(ref);
    }
    guardarVistaMovil(vista);
  };

  // Tocar un día (o un evento) del mes abre la semana de ese día con su
  // listado ya abierto abajo, sin otro toque
  const abrirSemanaDeDia = (dia) => {
    ponerSemana(dia);
    guardarVistaMovil('semana');
    setPanelMovil({ tipo: 'dia', fecha: diaISO(dia) });
  };

  const moverSemana = (delta) => ponerSemana(sumarDias(semanaFecha, 7 * delta));

  // Botón Hoy: vuelve al mes de hoy; en el móvil también a la semana de hoy y,
  // en Mes y Semana, abre el listado de hoy abajo. Atenuado si ya está todo así.
  const fechaHoy = new Date();
  const isoHoy = diaISO(fechaHoy);
  const enMesDeHoy = currentDate.getFullYear() === fechaHoy.getFullYear() && currentDate.getMonth() === fechaHoy.getMonth();
  const enSemanaDeHoy = diaISO(semanaFecha) === diaISO(lunesDeLaSemana(fechaHoy));
  const panelEnHoy = panelMovil?.tipo === 'dia' && panelMovil.fecha === isoHoy;
  let hoyYaALaVista = enMesDeHoy;
  if (isMobile && vistaMovil === 'semana') hoyYaALaVista = enSemanaDeHoy && panelEnHoy;
  else if (isMobile && vistaMovil === 'mes') hoyYaALaVista = enMesDeHoy && panelEnHoy;
  const irAHoy = () => {
    const hoy = new Date();
    if (!enMesDeHoy) setCurrentDate(hoy);
    if (!isMobile) return;
    setSemanaFecha(lunesDeLaSemana(hoy));
    if (vistaMovil === 'mes' || vistaMovil === 'semana') setPanelMovil({ tipo: 'dia', fecha: diaISO(hoy) });
  };

  const fechaDeISO = (iso) => {
    const [y, m, d] = String(iso).split('-').map(Number);
    return new Date(y, m - 1, d);
  };
  const tituloDia = (fecha) => `${NOMBRES_DIA[(fecha.getDay() + 6) % 7]} ${fecha.getDate()} de ${NOMBRES_MES[fecha.getMonth()]}`;
  const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;

  // Todas las jornadas de multievento cargadas que pasan el filtro (para las barras del mes)
  const jornadasVisibles = filtrarEventos(
    (Array.isArray(presupuestos) ? presupuestos : []).map(p => ({ ...p, es_jornada_multi: !!p.es_multi_evento }))
  ).filter(esJornada);

  // Ventana del evento, pestaña Dirección (solo dirección): el Por hacer del
  // presupuesto, calculado igual que en su ficha (utils/porHacerEvento.js). Se
  // vuelve a pedir cada vez que se abre la pestaña, para verlo al día.
  const idPresupuestoModal = selectedEvent ? (selectedEvent.presupuesto_id || selectedEvent.id) : null;
  useEffect(() => {
    if (!showModal || activeTab !== 'direccion' || !esDireccionEfectiva || !idPresupuestoModal) return undefined;
    let cancelado = false;
    setPorHacerEvento({ id: idPresupuestoModal, cargando: true, tareas: null, error: null });
    (async () => {
      try {
        const datos = await cargarDatosPorHacer(idPresupuestoModal);
        const catalogos = necesitaCatalogos(datos.presupuesto) ? await cargarCatalogosPorHacer() : null;
        const tareas = tareasPorHacerDeDatos(datos, { catalogos });
        if (!cancelado) setPorHacerEvento({ id: idPresupuestoModal, cargando: false, tareas, error: null });
      } catch (e) {
        if (!cancelado) setPorHacerEvento({ id: idPresupuestoModal, cargando: false, tareas: null, error: 'No se ha podido cargar lo que queda por hacer.' });
      }
    })();
    return () => { cancelado = true; };
  }, [showModal, activeTab, esDireccionEfectiva, idPresupuestoModal]);

  // Botón de una tarea -> pestaña de la ficha donde se resuelve (las mismas que
  // en la ficha); lo que allí abre una ventana va a Por hacer, donde está su botón
  const DESTINO_TAREA = {
    ir_lineas: { pestana: 'lineas' },
    ir_alergenos: { pestana: 'alergenos' },
    ir_personal: { pestana: 'logistica', nivel: 'quien' },
    ir_material: { pestana: 'material' },
    ir_compra: { pestana: 'compra' },
    ir_logistica: { pestana: 'logistica', nivel: 'lugar' },
    ir_horario: { pestana: 'logistica', nivel: 'horario' },
    ir_facturacion: { pestana: 'facturacion' },
  };
  const urlTareaFicha = (id, tarea) => {
    const destino = DESTINO_TAREA[tarea?.accion?.tipo] || { pestana: 'por_hacer' };
    const params = new URLSearchParams({ pestana: destino.pestana });
    if (destino.nivel) params.set('nivel', destino.nivel);
    return `/presupuestos/${id}?${params.toString()}`;
  };

  const renderDireccionEvento = (ev) => {
    const st = porHacerEvento;
    if (st.cargando || st.id !== idPresupuestoModal) {
      return <div className="py-8 text-center text-sm text-[#5B6B64]">Cargando lo que queda por hacer…</div>;
    }
    if (st.error || !st.tareas) {
      return <div className="py-8 text-center text-sm text-[#B42318]">{st.error || 'No se ha podido cargar.'}</div>;
    }
    const t = st.tareas;
    const indice = t.fases.findIndex(f => f.actual);
    const fases = (t.faseActual === null ? [] : t.fases.filter((_, i) => i <= indice))
      .map(f => ({ ...f, pendientes: f.tareas.filter(x => x.estado === 'pendiente') }))
      .filter(f => f.pendientes.length > 0);
    const hito = t.siguienteHito;
    const porcentaje = t.total ? Math.round((t.hechas / t.total) * 100) : 0;
    return (
      <div className="space-y-3" data-testid="evento-direccion">
        <div className="bg-white border border-[#E1E6E3] rounded-xl p-3">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-sm font-semibold text-[#1F2A26]">{t.hechas} de {t.total} tareas hechas</span>
            <span className="text-xs text-[#5B6B64]">{porcentaje} %</span>
          </div>
          <div className="mt-2 h-1.5 rounded-full bg-[#E1E6E3] overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${porcentaje}%`, backgroundColor: '#157A58' }} />
          </div>
          {hito && (
            <p className="text-xs text-[#5B6B64] mt-2">
              Siguiente: {hito.texto}{hito.fecha ? `, ${hito.fecha.getDate()} de ${NOMBRES_MES[hito.fecha.getMonth()]}` : ''}
            </p>
          )}
        </div>

        {fases.length === 0 ? (
          <div className="bg-white border border-[#E1E6E3] rounded-xl p-4 text-center text-sm font-semibold text-[#157A58]">
            Al día: no queda nada por hacer
          </div>
        ) : fases.map(f => (
          <div key={f.clave}>
            <h4 className="text-[11px] font-bold uppercase tracking-wide text-[#5B6B64] mb-1.5">{f.titulo}</h4>
            <div className="bg-white border border-[#E1E6E3] rounded-xl divide-y divide-[#EEF1EF] overflow-hidden">
              {f.pendientes.map(x => (
                <button
                  key={`${x.clave}-${x.jornada?.id || ''}`}
                  type="button"
                  onClick={() => { setShowModal(false); navigate(urlTareaFicha(idPresupuestoModal, x)); }}
                  data-uso="calendario.tarea_direccion"
                  className="w-full text-left px-3 py-2.5 min-h-[44px] flex items-start gap-2.5 hover:bg-[#F4F7F5]"
                >
                  <span
                    className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: x.prioridad === 'alta' ? '#B42318' : '#D97706' }}
                    aria-hidden="true"
                  />
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold text-[#1F2A26]">
                      {x.titulo}{x.jornada?.etiqueta ? ` · ${x.jornada.etiqueta}` : ''}
                    </span>
                    {x.descripcion && <span className="block text-xs text-[#5B6B64] mt-0.5">{x.descripcion}</span>}
                  </span>
                  <ChevronRight className="w-4 h-4 text-[#5B6B64] mt-1 flex-shrink-0" />
                </button>
              ))}
            </div>
          </div>
        ))}
        <p className="text-xs text-[#5B6B64]">
          Solo lo ve dirección. Es el Por hacer de la ficha{ev.es_jornada_multi ? ', del presupuesto entero (todas las jornadas)' : ''}; al tocar una tarea se abre la ficha donde se resuelve.
        </p>
      </div>
    );
  };

  // Semana móvil: al abrirla (o al cambiar de semana, llegar los datos o abrir
  // el listado de un día) se desplaza para que ese día, o hoy, quede a la vista
  const fechaListadoSemana = panelMovil?.tipo === 'dia' ? panelMovil.fecha : null;
  useEffect(() => {
    if (!isMobile || vistaMovil !== 'semana') return;
    const el = scrollSemanaRef.current;
    if (el) el.scrollLeft = Math.max(0, (hoySemanaRef.current || 0) - 8);
  }, [isMobile, vistaMovil, semanaFecha, presupuestos, fechaListadoSemana]);

  // Leyenda escrita (siempre visible) que además filtra, como las casillas de escritorio
  const renderLeyendaMovil = (conGrupo) => {
    const muestra = (estilo) => (
      <span className="inline-block w-[18px] h-3 rounded-[3px] box-border flex-shrink-0" style={estilo} aria-hidden="true" />
    );
    const casillas = [
      { clave: 'confirmados', texto: 'Confirmado', estilo: COLORES_MOVIL.confirmado },
      ...(isDirectivo ? [{ clave: 'pendientes', texto: 'Sin confirmar', estilo: COLORES_MOVIL.sinConfirmar }] : []),
      { clave: 'multievento', texto: 'Varios días', estilo: COLORES_MOVIL.jornadaConfirmada },
      { clave: 'ausencias', texto: 'Ausencias', estilo: { backgroundColor: '#FBBF24' } },
    ];
    return (
      <div
        className="flex flex-wrap items-center justify-center gap-x-3 px-2 py-1 border-t border-[#E1E6E3] text-xs text-[#3B4A44]"
        data-testid="calendario-filtros"
      >
        {casillas.map(c => (
          <label key={c.clave} className={`inline-flex items-center gap-1.5 min-h-[44px] cursor-pointer select-none ${filtros[c.clave] ? '' : 'opacity-50'}`}>
            <input
              type="checkbox"
              checked={!!filtros[c.clave]}
              onChange={() => cambiarFiltro(c.clave)}
              className="w-4 h-4 accent-duetto-primary"
              data-testid={`calendario-filtro-${c.clave}`}
            />
            {muestra(c.estilo)}
            {c.texto}
          </label>
        ))}
        {conGrupo && (
          <span className="inline-flex items-center gap-1.5 min-h-[44px]">
            {muestra(COLORES_MOVIL.grupo)}
            Más a la vez
          </span>
        )}
      </div>
    );
  };

  // MES: pastillas con el nombre corto (todas: la semana crece lo que haga
  // falta), barras continuas para los multievento y una línea ámbar con las ausencias
  const renderMesMovil = () => {
    const semanas = semanasDelMes(currentDate);
    const enMes = (d) => d.getFullYear() === currentDate.getFullYear() && d.getMonth() === currentDate.getMonth();
    const hoyISO = diaISO(new Date());
    return (
      <div className="-mx-4 bg-white shadow-sm" data-testid="calendario-mes-movil">
        <div className="grid grid-cols-7 text-center text-[11px] font-semibold text-[#5B6B64] px-1 pt-2 pb-1.5 border-b border-[#E1E6E3]">
          {['LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB', 'DOM'].map(d => <div key={d}>{d}</div>)}
        </div>
        <div className="px-1">
          {semanas.map(dias => {
            const eventosDia = dias.map(d => (enMes(d) ? getEventsForDay(d) : []));
            const ausenciasDia = dias.map(d => (enMes(d) ? getAusenciasForDay(d) : []));
            const { carriles, barras } = barrasMultieventoSemana(dias, enMes, jornadasVisibles);
            const filas = `24px ${'16px '.repeat(carriles)}minmax(36px, auto)`;
            const filaContenido = carriles + 2;
            return (
              <div
                key={diaISO(dias[0])}
                className="relative grid grid-cols-7 border-b border-[#EEF1EF] pb-1"
                style={{ gridTemplateRows: filas, rowGap: 2 }}
              >
                {/* Capa de fondo: la celda entera es el botón que abre la semana */}
                {dias.map((d, i) => {
                  const iso = diaISO(d);
                  const delMes = enMes(d);
                  const n = eventosDia[i].length;
                  return (
                    <button
                      key={`btn-${iso}`}
                      type="button"
                      onClick={() => abrirSemanaDeDia(d)}
                      data-foco-fecha={delMes && fechaFoco === iso ? fechaFoco : undefined}
                      data-testid={delMes ? `calendar-day-${d.getDate()}` : undefined}
                      aria-label={`${d.getDate()} de ${NOMBRES_MES[d.getMonth()]}: ${plural(n, 'evento', 'eventos')}${ausenciasDia[i].length ? `, ${plural(ausenciasDia[i].length, 'ausencia', 'ausencias')}` : ''}. Ver la semana`}
                      className={`relative z-0 w-full h-full min-h-[44px] active:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#157A58] ${delMes && fechaFoco === iso ? 'ring-2 ring-inset ring-[#157A58]' : ''}`}
                      style={{ gridColumn: i + 1, gridRow: '1 / -1' }}
                    />
                  );
                })}
                {/* Número del día */}
                {dias.map((d, i) => {
                  const esHoy = diaISO(d) === hoyISO;
                  return (
                    <span
                      key={`num-${diaISO(d)}`}
                      className="relative z-10 pointer-events-none self-center justify-self-center w-[22px] h-[22px] rounded-full flex items-center justify-center text-xs"
                      style={{
                        gridColumn: i + 1,
                        gridRow: 1,
                        ...(esHoy
                          ? { backgroundColor: '#1F2A26', color: '#FFFFFF', fontWeight: 700 }
                          : { color: enMes(d) ? '#1F2A26' : '#A7B0AC', fontWeight: enMes(d) ? 600 : 400 }),
                      }}
                    >
                      {d.getDate()}
                    </span>
                  );
                })}
                {/* Multievento: barra continua por carriles */}
                {barras.map(b => {
                  const estilo = b.conf ? COLORES_MOVIL.jornadaConfirmada : COLORES_MOVIL.jornadaSinConfirmar;
                  const discontinuo = !b.conf ? '1px dashed #7B5CC4' : undefined;
                  return (
                    <span
                      key={`barra-${b.clave}-${b.desde}`}
                      className="relative z-10 pointer-events-none block h-4 box-border text-[10px] font-bold leading-[14px] whitespace-nowrap overflow-hidden text-ellipsis pl-1"
                      style={{
                        gridColumn: `${b.desde + 1} / ${b.hasta + 2}`,
                        gridRow: b.carril + 2,
                        backgroundColor: estilo.backgroundColor,
                        color: estilo.color,
                        lineHeight: b.conf ? '16px' : '14px',
                        borderTop: discontinuo,
                        borderBottom: discontinuo,
                        borderLeft: !b.sigueAntes ? discontinuo : undefined,
                        borderRight: !b.sigueDespues ? discontinuo : undefined,
                        marginLeft: b.sigueAntes ? 0 : 2,
                        marginRight: b.sigueDespues ? 0 : 2,
                        borderRadius: `${b.sigueAntes ? 0 : 4}px ${b.sigueDespues ? 0 : 4}px ${b.sigueDespues ? 0 : 4}px ${b.sigueAntes ? 0 : 4}px`,
                      }}
                    >
                      {b.texto}
                    </span>
                  );
                })}
                {/* Todos los eventos del día y sus ausencias */}
                {dias.map((d, i) => {
                  const sueltos = eventosDia[i]
                    .filter(e => !esJornada(e))
                    .sort((x, y) => String(x.hora_inicio || '99').localeCompare(String(y.hora_inicio || '99')));
                  const nAus = ausenciasDia[i].length;
                  if (sueltos.length === 0 && nAus === 0) return null;
                  return (
                    <div
                      key={`cont-${diaISO(d)}`}
                      className="relative z-10 pointer-events-none flex flex-col gap-0.5 min-w-0"
                      style={{ gridColumn: i + 1, gridRow: filaContenido }}
                    >
                      {sueltos.map(e => {
                        const conf = esConfirmado(e);
                        return (
                          <span
                            key={claveEvento(e)}
                            className="block mx-0.5 h-4 box-border rounded text-[10px] font-semibold px-[3px] whitespace-nowrap overflow-hidden"
                            style={{
                              ...(conf ? COLORES_MOVIL.confirmado : { ...COLORES_MOVIL.sinConfirmar, border: '1px dashed #6B7872' }),
                              lineHeight: conf ? '16px' : '14px',
                            }}
                          >
                            {nombreCorto(e.cliente_nombre)}
                          </span>
                        );
                      })}
                      {nAus > 0 && (
                        soloDiasOcupados(ausenciasDia[i]) ? (
                          <span className="mx-0.5 border-t-2 text-[9px] font-semibold leading-tight whitespace-nowrap overflow-hidden" style={{ borderColor: ESTILO_DIA_OCUPADO.backgroundColor, color: ESTILO_DIA_OCUPADO.color }}>
                            {nAus === 1 ? etiquetaAusencia(ausenciasDia[i][0]) : plural(nAus, 'ausencia', 'ausencias')}
                          </span>
                        ) : (
                          <span className="mx-0.5 border-t-2 border-amber-400 text-[9px] font-semibold text-amber-800 leading-tight whitespace-nowrap overflow-hidden">
                            {plural(nAus, 'ausencia', 'ausencias')}
                          </span>
                        )
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
        {renderLeyendaMovil(false)}
      </div>
    );
  };

  // SEMANA: eje de horas y bloques por hora; los eventos que coinciden van en
  // carriles (confirmados primero), todos visibles y del mismo ancho: el día se
  // ensancha y la semana se desplaza en horizontal
  const renderSemanaMovil = () => {
    const dias = diasDeLaSemana(semanaFecha);
    const hoyISO = diaISO(new Date());
    const eventosDia = dias.map(d => getEventsForDay(d).map(ev => ({ ev, tramo: tramoEvento(ev) })));
    const ausenciasDia = dias.map(d => getAusenciasForDay(d));
    const sinHoraDia = eventosDia.map(lista => lista.filter(x => !x.tramo).map(x => x.ev));
    const { desde, hasta } = ejeHoras(eventosDia.flat().map(x => x.tramo));
    const horas = [];
    for (let h = desde; h <= hasta; h += 1) horas.push(h);
    const hayFranja = ausenciasDia.some(l => l.length > 0) || sinHoraDia.some(l => l.length > 0);
    const altoRejilla = (hasta - desde) * PX_HORA + 16;

    // Carriles de cada día y su ancho; posición de cada día en la semana
    const gruposDia = eventosDia.map(lista => repartir(lista
      .filter(x => x.tramo)
      .map(x => ({ a: x.tramo.a, b: x.tramo.b, conf: esConfirmado(x.ev), ev: x.ev }))));
    const anchos = gruposDia.map(grupos => {
      const carriles = Math.max(0, ...grupos.map(g => g.n));
      return carriles ? carriles * ANCHO_CARRIL : ANCHO_DIA_VACIO;
    });
    const izquierdas = anchos.map((_, i) => ANCHO_HORAS + anchos.slice(0, i).reduce((t, w) => t + w, 0));
    const anchoTotal = ANCHO_HORAS + anchos.reduce((t, w) => t + w, 0) + 4;
    // Día a la vista al abrir: el del listado abierto (tocado en el mes) o hoy
    const iFoco = dias.findIndex(d => diaISO(d) === (panelMovil?.tipo === 'dia' ? panelMovil.fecha : hoyISO));
    hoySemanaRef.current = iFoco >= 0 ? izquierdas[iFoco] - ANCHO_HORAS : 0;
    const columnas = `${ANCHO_HORAS}px ${anchos.map(w => `${w}px`).join(' ')}`;

    const bloques = [];
    gruposDia.forEach((grupos, d) => {
      const iso = diaISO(dias[d]);
      grupos.forEach(g => {
        const anchoCarril = anchos[d] / g.n;
        g.items.forEach(x => {
          const ev = x.ev;
          bloques.push(
            <button
              key={`ev-${claveEvento(ev)}`}
              type="button"
              onClick={() => setPanelMovil({ tipo: 'claves', fecha: iso, claves: [claveEvento(ev)] })}
              aria-label={`${ev.numero || ''} ${ev.cliente_nombre || 'Cliente'}, ${ev.hora_inicio || ''}`}
              data-testid={`semana-bloque-${ev.id}`}
              data-tour="cal-bloque"
              className="absolute box-border rounded-[5px] px-1 py-[3px] flex flex-col items-start gap-px overflow-hidden text-left"
              style={{
                top: (x.a - desde) * PX_HORA + 9,
                height: Math.max((x.b - x.a) * PX_HORA - 2, 30),
                left: izquierdas[d] + x.carril * anchoCarril + 1,
                width: anchoCarril - 2,
                ...estiloEventoMovil(ev),
              }}
            >
              <span className="text-[10px] font-bold leading-[1.1] whitespace-nowrap">{ev.numero || ''}</span>
              <span className="text-[10px] leading-[1.15] opacity-90 max-w-full overflow-hidden" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                {nombreCorto(ev.cliente_nombre, 22)}
              </span>
            </button>
          );
        });
      });
    });

    // Columna de la izquierda (horas): queda fija al desplazar la semana
    const fijaIzquierda = 'sticky left-0 z-20 bg-white';

    return (
      <div className="-mx-4 bg-white shadow-sm" data-testid="calendario-semana-movil">
        <div ref={scrollSemanaRef} className="overflow-x-auto overscroll-x-contain" data-testid="semana-desplazable">
          <div style={{ width: anchoTotal }}>
            {/* Cabecera: letra, número (hoy en círculo) y número de eventos (rojo con 4 o más) */}
            <div className="grid border-b border-[#E1E6E3]" style={{ gridTemplateColumns: columnas }}>
              <div className={fijaIzquierda} />
              {dias.map((d, i) => {
                const iso = diaISO(d);
                const n = eventosDia[i].length;
                const esHoy = iso === hoyISO;
                return (
                  <button
                    key={iso}
                    type="button"
                    onClick={() => setPanelMovil({ tipo: 'dia', fecha: iso })}
                    data-foco-fecha={fechaFoco === iso ? fechaFoco : undefined}
                    data-testid={`semana-dia-${iso}`}
                    aria-label={`${NOMBRES_DIA[i]} ${d.getDate()}: ${plural(n, 'evento', 'eventos')}`}
                    className="min-h-[44px] pt-1 pb-1.5 flex items-start border-l border-[#EEF1EF] active:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#157A58]"
                  >
                    {/* En un día ancho, el nombre del día se queda a la vista al desplazar */}
                    <span className="sticky flex flex-col items-center gap-0.5 ml-1" style={{ left: ANCHO_HORAS + 4, minWidth: Math.min(anchos[i], ANCHO_CARRIL) - 8 }}>
                    <span className="text-[11px] font-semibold text-[#5B6B64]">{LETRAS_DIA[i]}</span>
                    <span
                      className="w-7 h-7 rounded-full flex items-center justify-center text-[15px] font-bold"
                      style={esHoy ? { backgroundColor: '#1F2A26', color: '#FFFFFF' } : { color: d.getMonth() === currentDate.getMonth() ? '#1F2A26' : '#A7B0AC' }}
                    >
                      {d.getDate()}
                    </span>
                    <span
                      className="h-4 min-w-[16px] box-border px-1 rounded-lg text-[10px] font-bold leading-4 text-center"
                      style={n >= 4 ? { backgroundColor: '#B42318', color: '#FFFFFF' } : (n ? { backgroundColor: '#E1E6E3', color: '#1F2A26' } : { backgroundColor: 'transparent' })}
                    >
                      {n ? n : ''}
                    </span>
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Franja de arriba: ausencias (ámbar) y eventos sin hora */}
            {hayFranja && (
              <div className="grid py-1 gap-y-0.5 border-b border-[#EEF1EF]" style={{ gridTemplateColumns: columnas }}>
                <div className={fijaIzquierda} />
                {dias.map((d, i) => {
                  const iso = diaISO(d);
                  const nAus = ausenciasDia[i].length;
                  const nSin = sinHoraDia[i].length;
                  return (
                    <div key={`franja-${iso}`} className="flex flex-col gap-0.5 px-px min-w-0">
                      {nAus > 0 && (
                        <button
                          type="button"
                          onClick={() => setPanelMovil({ tipo: 'ausencias', fecha: iso })}
                          aria-label={`${tituloDia(d)}: ${plural(nAus, 'ausencia', 'ausencias')}. Ver quién`}
                          data-testid={`semana-ausencias-${iso}`}
                          className={`min-h-[28px] rounded text-[10px] font-bold leading-tight whitespace-nowrap overflow-hidden ${soloDiasOcupados(ausenciasDia[i]) ? '' : 'bg-amber-400 text-amber-950'}`}
                          style={soloDiasOcupados(ausenciasDia[i]) ? ESTILO_DIA_OCUPADO : undefined}
                        >
                          {soloDiasOcupados(ausenciasDia[i]) && nAus === 1 ? etiquetaAusencia(ausenciasDia[i][0]) : plural(nAus, 'ausencia', 'ausencias')}
                        </button>
                      )}
                      {nSin > 0 && (
                        <button
                          type="button"
                          onClick={() => setPanelMovil({ tipo: 'dia', fecha: iso })}
                          aria-label={`${tituloDia(d)}: ${plural(nSin, 'evento sin hora', 'eventos sin hora')}`}
                          className="min-h-[28px] rounded text-[10px] font-bold leading-tight text-[#1F2A26] bg-[#F4F7F5] border border-dashed border-[#6B7872] overflow-hidden"
                        >
                          {nSin} sin hora
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Rejilla de horas con los bloques */}
            <div className="relative" style={{ height: altoRejilla }}>
              {dias.map((d, i) => (
                <div
                  key={`sep-${diaISO(d)}`}
                  className="absolute top-0 bottom-0 w-px bg-[#EEF1EF] pointer-events-none"
                  style={{ left: izquierdas[i] }}
                />
              ))}
              {horas.map(h => (
                <div key={`h-${h}`} className="absolute left-0 right-0 flex items-start pointer-events-none" style={{ top: (h - desde) * PX_HORA + 8 }}>
                  <span className={`${fijaIzquierda} w-[34px] text-right pr-[5px] box-border text-[10px] text-[#5B6B64] -translate-y-1.5`}>{textoHora(h)}</span>
                  <span className="flex-1 border-t border-[#EEF1EF]" />
                </div>
              ))}
              {bloques}
              {/* Tapa blanca bajo las horas para que los bloques no asomen al desplazar */}
              <div className="sticky left-0 top-0 z-10 w-[34px] h-full bg-white pointer-events-none" />
            </div>
          </div>
        </div>
        {renderLeyendaMovil(false)}
      </div>
    );
  };

  // Panel inferior: lista de lo tocado (un bloque, un "+N", un día o sus ausencias)
  const renderPanelMovil = () => {
    if (!panelMovil) return null;
    const fecha = fechaDeISO(panelMovil.fecha);
    const delDia = getEventsForDay(fecha);
    const ausDia = getAusenciasForDay(fecha);
    let eventos = [];
    let aus = [];
    let subtitulo = '';
    if (panelMovil.tipo === 'ausencias') {
      aus = ausDia;
      subtitulo = plural(aus.length, 'ausencia', 'ausencias');
    } else if (panelMovil.tipo === 'claves') {
      eventos = delDia.filter(e => (panelMovil.claves || []).includes(claveEvento(e)));
      subtitulo = panelMovil.grupo
        ? `${plural(eventos.length, 'evento coincide', 'eventos coinciden')} en esta franja`
        : 'Toca otro bloque o el día para ver más';
    } else {
      eventos = delDia;
      aus = ausDia;
      subtitulo = plural(eventos.length, 'evento', 'eventos') + (aus.length ? ` · ${plural(aus.length, 'ausencia', 'ausencias')}` : '');
    }
    const filas = eventos
      .map(ev => ({ ev, tramo: tramoEvento(ev) }))
      .sort((x, y) => (x.tramo ? x.tramo.a : -1) - (y.tramo ? y.tramo.a : -1));
    return (
      <section
        aria-label="Detalle del día"
        data-testid="calendario-panel-movil"
        className="fixed left-0 right-0 bottom-0 z-40 max-h-[60dvh] bg-white rounded-t-[18px] flex flex-col"
        style={{ boxShadow: '0 -6px 24px rgba(31,42,38,0.18)' }}
      >
        <div className="flex items-center gap-2 pl-4 pr-2 pt-3 pb-2 border-b border-[#EEF1EF]">
          <div className="flex-1 min-w-0">
            <div className="text-base font-bold text-[#1F2A26]">{tituloDia(fecha)}</div>
            <div className="text-xs text-[#5B6B64]">{subtitulo}</div>
          </div>
          <button
            type="button"
            onClick={() => setPanelMovil(null)}
            aria-label="Cerrar detalle"
            data-testid="calendario-panel-cerrar"
            className="w-11 h-11 flex items-center justify-center rounded-lg active:bg-gray-100"
          >
            <X className="w-5 h-5 text-[#1F2A26]" />
          </button>
        </div>
        <div className="overflow-y-auto px-4 pt-1 pb-4">
          {aus.length > 0 && (
            <div className="my-2 bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1" data-testid="calendario-panel-ausencias">
              {aus.map(a => (
                esDiaOcupado(a) ? (
                  <p key={`panel-aus-${a.solicitud_id}-${a.fecha}`} className="text-sm font-semibold rounded px-2 py-1" style={ESTILO_DIA_OCUPADO}>
                    {textoAusenciaLargo(a)}
                  </p>
                ) : (
                  <p key={`panel-aus-${a.solicitud_id}-${a.fecha}`} className="text-sm text-amber-900">
                    <span className="font-semibold">{a.empleado_nombre}</span> · {etiquetaAusencia(a)}
                    <span className="block text-xs text-amber-800">Del {a.fecha_inicio} al {a.fecha_fin}</span>
                  </p>
                )
              ))}
            </div>
          )}
          {filas.length === 0 && aus.length === 0 && (
            <p className="py-6 text-center text-sm text-[#5B6B64]">No hay eventos este día</p>
          )}
          {filas.map(({ ev, tramo }) => {
            const conf = esConfirmado(ev);
            return (
              <button
                key={`panel-${claveEvento(ev)}`}
                type="button"
                onClick={() => abrirEventoDesde(ev)}
                data-uso="calendario.abrir"
                data-uso-detalle={vistaMovil === 'semana' ? 'semana' : 'panel'}
                data-tour="cal-evento"
                data-testid={`panel-event-${ev.id}`}
                className="w-full min-h-[44px] flex items-center gap-2.5 py-2.5 border-b border-[#EEF1EF] text-left text-[#1F2A26] active:bg-gray-50"
              >
                <div className="w-[52px] flex-shrink-0 text-[13px] font-bold leading-tight">
                  {tramo ? textoHora(tramo.a) : 'Sin hora'}
                  {tramo && (
                    <div className="text-[11px] font-medium text-[#5B6B64]">a {textoHora(tramo.b)}{tramo.provisional ? '*' : ''}</div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">
                    {ev.numero ? `${ev.numero} · ` : ''}{ev.cliente_nombre || 'Cliente'}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pt-0.5 text-xs text-[#3B4A44]">
                    {ev.num_personas ? <span>{ev.num_personas} pax</span> : null}
                    <span
                      className="text-[11px] font-bold rounded-full box-border"
                      style={conf
                        ? { backgroundColor: '#157A58', color: '#FFFFFF', padding: '1px 7px' }
                        : { border: '1.5px dashed #6B7872', color: '#3B4A44', padding: '0 5.5px' }}
                    >
                      {conf ? 'Confirmado' : 'Sin confirmar'}
                    </span>
                    {esJornada(ev) && (
                      <span className="text-[11px] font-bold rounded-full px-[7px] py-px bg-[#EFEAFB] text-[#5B3FA6]">
                        Día {ev.jornada_numero} de {ev.total_jornadas}
                      </span>
                    )}
                    {tramo && tramo.provisional && (
                      <span className="text-[11px] font-semibold rounded-full px-[7px] py-px bg-amber-100 text-amber-900" data-testid="calendario-fin-provisional">
                        *Hora de fin provisional
                      </span>
                    )}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-[#A7B0AC] flex-shrink-0" aria-hidden="true" />
              </button>
            );
          })}
        </div>
      </section>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {dialogoHorarios}
      {/* Header móvil: selector Mes | Semana | Lista y el periodo con sus flechas */}
      {isMobile ? (
      <div className="bg-white shadow-sm">
        <div className="px-4 pt-3 pb-1">
          <div className="flex items-center justify-between gap-2">
            <h1 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#157A58]" />
              Eventos
            </h1>
            <div role="group" aria-label="Vista" className="flex bg-[#F4F7F5] rounded-[10px] p-[3px]" data-tour="cal-lista" data-testid="calendario-vista-movil">
              {[['mes', 'Mes'], ['semana', 'Semana'], ['lista', 'Lista']].map(([v, texto]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => elegirVistaMovil(v)}
                  aria-pressed={vistaMovil === v}
                  data-testid={`calendario-vista-${v}`}
                  className={`h-11 px-3 rounded-lg text-sm flex items-center ${vistaMovil === v ? 'bg-white font-bold text-[#1F2A26]' : 'font-semibold text-[#5B6B64]'}`}
                  style={vistaMovil === v ? { boxShadow: '0 1px 2px rgba(31,42,38,0.12)' } : undefined}
                >
                  {texto}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center mt-1">
            <div className="flex-1 min-w-0 text-base font-bold text-[#1F2A26] truncate" data-testid="calendario-periodo">
              {vistaMovil === 'semana'
                ? textoRangoSemana(diasDeLaSemana(semanaFecha))
                : `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`}
            </div>
            <button
              type="button"
              onClick={irAHoy}
              disabled={hoyYaALaVista}
              className="h-11 px-3 mr-1 flex items-center rounded-full text-sm font-semibold text-[#1F2A26] active:bg-gray-100 disabled:opacity-40 disabled:active:bg-transparent"
              aria-label={vistaMovil === 'semana' ? 'Ir a la semana de hoy' : 'Ir al mes de hoy'}
              data-uso="calendario.hoy"
              data-guia="calendario-hoy"
            >
              Hoy
            </button>
            <button
              type="button"
              onClick={vistaMovil === 'semana' ? () => moverSemana(-1) : prevMonth}
              className="w-11 h-11 flex items-center justify-center rounded-full active:bg-gray-100"
              aria-label={vistaMovil === 'semana' ? 'Semana anterior' : 'Mes anterior'}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={vistaMovil === 'semana' ? () => moverSemana(1) : nextMonth}
              className="w-11 h-11 flex items-center justify-center rounded-full active:bg-gray-100"
              aria-label={vistaMovil === 'semana' ? 'Semana siguiente' : 'Mes siguiente'}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
      ) : (
      <div className="bg-white shadow-sm">
        <div className="px-4 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              Eventos
            </h1>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={irAHoy}
                disabled={hoyYaALaVista}
                className="px-3 py-1.5 text-sm font-medium hover:bg-gray-100 rounded-full disabled:opacity-40 disabled:hover:bg-transparent disabled:cursor-default"
                aria-label="Ir al mes de hoy"
                data-uso="calendario.hoy"
                data-guia="calendario-hoy"
              >
                Hoy
              </button>
              <button onClick={prevMonth} className="p-2.5 hover:bg-gray-100 rounded-full" aria-label="Mes anterior">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-sm font-medium min-w-[100px] text-center">
                {monthNames[currentDate.getMonth()].slice(0, 3)} {currentDate.getFullYear()}
              </span>
              <button onClick={nextMonth} className="p-2.5 hover:bg-gray-100 rounded-full" aria-label="Mes siguiente">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
      )}

      <div className="p-4">
        {/* Stats Cards - Horizontal scroll en móvil (en el móvil, solo en la Lista) */}
        {(!isMobile || vistaMovil === 'lista') && (
        <div className="flex gap-3 mb-4 overflow-x-auto pb-2">
          <div className="flex-shrink-0 bg-white rounded-xl p-3 shadow-sm min-w-[100px]">
            <div className="text-2xl font-bold text-blue-600">{monthEvents.filter(e => e.estado === 'confirmado').length}</div>
            <div className="text-xs text-gray-500">Confirmados</div>
          </div>
          {isDirectivo && monthEvents.filter(e => e.estado !== 'confirmado').length > 0 && (
            <div className="flex-shrink-0 bg-white rounded-xl p-3 shadow-sm min-w-[100px]">
              <div className="text-2xl font-bold text-gray-400">{monthEvents.filter(e => e.estado !== 'confirmado').length}</div>
              <div className="text-xs text-gray-500">Pendientes</div>
            </div>
          )}
          <div className="flex-shrink-0 bg-white rounded-xl p-3 shadow-sm min-w-[100px]">
            <div className="text-2xl font-bold text-green-600">
              {monthEvents.filter(e => e.estado === 'confirmado').reduce((sum, p) => sum + (p.num_personas || 0), 0)}
            </div>
            <div className="text-xs text-gray-500">Personas</div>
          </div>
          <div className="flex-shrink-0 bg-white rounded-xl p-3 shadow-sm min-w-[100px]">
            <div className="text-2xl font-bold text-orange-600">
              {new Set(monthEvents.map(p => p.cliente_id)).size}
            </div>
            <div className="text-xs text-gray-500">Clientes</div>
          </div>
        </div>
        )}

        {/* Leyenda de colores con casillas: desmarcar una oculta ese tipo en el calendario y en la lista
            (en Mes y Semana del móvil va debajo, con los colores de esas vistas) */}
        {(!isMobile || vistaMovil === 'lista') && (
        <div className="flex flex-wrap gap-x-4 gap-y-2 mb-4 text-xs text-gray-700" data-testid="calendario-filtros">
          {[
            { clave: 'confirmados', color: 'bg-blue-500', texto: 'Confirmado' },
            { clave: 'multievento', color: 'bg-purple-500', texto: 'Multi-evento' },
            ...(isDirectivo ? [{ clave: 'pendientes', color: 'bg-gray-400', texto: 'Pendiente' }] : []),
            { clave: 'ausencias', color: 'bg-amber-400', texto: 'Vacaciones / ausencias' },
          ].map(f => (
            <label key={f.clave} className={`flex items-center gap-1.5 py-1.5 cursor-pointer select-none ${filtros[f.clave] ? '' : 'opacity-50'}`}>
              <input
                type="checkbox"
                checked={!!filtros[f.clave]}
                onChange={() => cambiarFiltro(f.clave)}
                className="w-5 h-5 sm:w-3.5 sm:h-3.5 accent-duetto-primary"
                data-testid={`calendario-filtro-${f.clave}`}
              />
              <span className={`w-3 h-3 rounded ${f.color}`}></span> {f.texto}
            </label>
          ))}
        </div>
        )}

        {/* Móvil: Mes y Semana */}
        {isMobile && vistaMovil === 'mes' && renderMesMovil()}
        {isMobile && vistaMovil === 'semana' && renderSemanaMovil()}

        {/* Vista Lista (mejor para móvil) */}
        {isMobile && vistaMovil === 'lista' && (
          <div className="space-y-3">
            {getAusenciasResumenMes().length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                <p className="text-xs font-semibold text-amber-900 mb-1">Ausencias este mes</p>
                {getAusenciasResumenMes().map(a => (
                  esDiaOcupado(a) ? (
                    <p key={`res-${a.solicitud_id}`} className="text-xs font-semibold rounded px-1.5 py-0.5 my-0.5" style={ESTILO_DIA_OCUPADO}>
                      {textoAusenciaLargo(a)}: {a.fecha_inicio}{a.fecha_fin && a.fecha_fin !== a.fecha_inicio ? ` a ${a.fecha_fin}` : ''}
                    </p>
                  ) : (
                    <p key={`res-${a.solicitud_id}`} className="text-xs text-amber-800">
                      {etiquetaAusencia(a)} · {a.empleado_nombre}: {a.fecha_inicio} a {a.fecha_fin}
                    </p>
                  )
                ))}
              </div>
            )}
            {monthEvents.length === 0 ? (
              <div className="bg-white rounded-xl p-8 text-center shadow-sm">
                <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                <p className="text-gray-500">No hay eventos este mes</p>
              </div>
            ) : (
              monthEvents.map(event => {
                const isPending = event.estado !== 'confirmado';
                return (
                <button
                  key={event.id + (event.jornada_id || '') + event.fecha_evento}
                  onClick={() => abrirEventoDesde(event)}
                  data-testid={`list-event-${event.id}`}
                  data-uso="calendario.abrir"
                  data-uso-detalle="lista"
                  data-tour="cal-evento"
                  data-foco-fecha={claveFocoLista === event.id + (event.jornada_id || '') + event.fecha_evento ? fechaFoco : undefined}
                  className={`w-full rounded-xl p-4 shadow-sm text-left active:bg-gray-50 transition ${
                    isPending 
                      ? 'bg-gray-50 border-l-4 border-gray-400'
                      : event.es_jornada_multi 
                        ? 'bg-purple-50 border-l-4 border-purple-500' 
                        : 'bg-white'
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`text-sm font-medium ${
                        isPending ? 'text-gray-500' : event.es_jornada_multi ? 'text-purple-600' : 'text-blue-600'
                      }`}>
                        {formatDate(event.fecha_evento)}
                      </div>
                      {event.es_jornada_multi && (
                        <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded text-xs font-medium">
                          Día {event.jornada_numero}/{event.total_jornadas}
                        </span>
                      )}
                      {isPending && (
                        <span className="bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded text-xs font-medium">
                          Pendiente
                        </span>
                      )}
                    </div>
                    <div className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      isPending
                        ? 'bg-gray-200 text-gray-600'
                        : event.es_jornada_multi 
                          ? 'bg-purple-100 text-purple-700' 
                          : 'bg-blue-100 text-blue-700'
                    }`}>
                      {event.num_personas} pax
                    </div>
                  </div>
                  <div className="font-semibold text-gray-900 mb-1 line-clamp-2">
                    {event.cliente_nombre || 'Cliente'}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {event.hora_inicio || '—'}{event.hora_fin ? ` - ${event.hora_fin}` : ''}
                    </span>
                    {event.lugar_evento && (
                      <span className="flex items-center gap-1 truncate">
                        <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="truncate">{event.lugar_evento.split(',')[0]}</span>
                      </span>
                    )}
                  </div>
                </button>
                );
              })
            )}
          </div>
        )}

        {/* Vista Calendario (ordenador) */}
        {!isMobile && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            {/* Day headers */}
            <div className="grid grid-cols-7 bg-gray-50 border-b">
              {dayNames.map(day => (
                <div key={day} className="py-2 text-center text-xs font-medium text-gray-500">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar days */}
            <div className="grid grid-cols-7">
              {days.map((day, index) => {
                const events = getEventsForDay(day);
                const ausenciasDia = getAusenciasForDay(day);
                const isToday = day?.toDateString() === new Date().toDateString();

                const esFoco = !!day && !!fechaFoco && isoDia(day) === fechaFoco;

                return (
                  <div
                    key={index}
                    data-foco-fecha={esFoco ? fechaFoco : undefined}
                    className={`min-h-[70px] sm:min-h-[100px] border-b border-r border-gray-100 p-1 align-top ${
                      !day ? 'bg-gray-50' : ''
                    } ${isToday ? 'bg-blue-50' : ''} ${esFoco ? 'ring-2 ring-inset ring-blue-400' : ''}`}
                  >
                    {day && (
                      <>
                        {/* El número del día abre la lista completa del día (útil en móvil) */}
                        <button
                          type="button"
                          onClick={() => events.length > 0 && setDayEventsModal({ date: day, events, ausencias: ausenciasDia })}
                          className={`text-xs font-medium mb-1 ${isToday ? 'text-blue-600' : 'text-gray-500'} ${events.length > 0 ? 'hover:underline' : 'cursor-default'}`}
                          title={events.length > 0 ? `${events.length} ${events.length === 1 ? 'evento' : 'eventos'}: ver la lista del día` : undefined}
                          data-testid={`calendar-day-${day.getDate()}`}
                        >
                          {day.getDate()}{events.length > 1 ? ` (${events.length})` : ''}
                        </button>
                        <div className="space-y-0.5">
                          {/* Ausencias del equipo (vacaciones, permisos...) */}
                          {ausenciasDia.map(a => (
                            <div
                              key={`aus-${a.solicitud_id}-${a.fecha}`}
                              title={esDiaOcupado(a)
                                ? textoAusenciaLargo(a)
                                : `${etiquetaAusencia(a)}: ${a.empleado_nombre} (${a.fecha_inicio} a ${a.fecha_fin})`}
                              className={`w-full px-1 py-0.5 rounded text-[9px] truncate leading-tight font-semibold ${esDiaOcupado(a) ? '' : 'bg-amber-400 text-amber-950'}`}
                              style={esDiaOcupado(a) ? ESTILO_DIA_OCUPADO : undefined}
                              data-testid={esDiaOcupado(a) ? `calendario-dia-ocupado-${a.solicitud_id}` : undefined}
                            >
                              {textoAusenciaCorto(a)}
                            </div>
                          ))}
                          {/* Todos los eventos del día a la vista (la celda crece); antes se cortaba
                              a 5 en escritorio y 3 en móvil con un "+N más" */}
                          {events.map(event => {
                            const isPending = event.estado !== 'confirmado';
                            const colorClass = isPending 
                              ? 'bg-gray-400 hover:bg-gray-500' 
                              : event.es_jornada_multi 
                                ? 'bg-purple-500 hover:bg-purple-600' 
                                : 'bg-blue-500 hover:bg-blue-600';
                            return (
                            <button
                              key={event.id + (event.jornada_id || '') + event.fecha_evento}
                              onClick={() => abrirEventoDesde(event)}
                              data-testid={`calendar-event-${event.id}`}
                              data-uso="calendario.abrir"
                              data-uso-detalle="mes"
                              data-tour="cal-evento"
                              className={`w-full text-left px-1 py-0.5 text-white rounded text-[9px] truncate leading-tight ${colorClass}`}
                            >
                              {event.es_jornada_multi && (
                                <span className="font-bold">[{event.jornada_numero}/{event.total_jornadas}] </span>
                              )}
                              {isPending && <span className="font-bold">[P] </span>}
                              {event.num_personas}p - {event.cliente_nombre?.split(' ')[0] || 'Evento'}
                            </button>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Hueco para poder subir lo que tapa el panel inferior del móvil */}
        {isMobile && panelMovil && <div className="h-[60dvh]" aria-hidden="true" />}
      </div>

      {/* Panel inferior del móvil (lo tocado en la semana) */}
      {isMobile && renderPanelMovil()}

      {/* Ventana del evento (diseño nuevo): cabecera blanca con estado, pestañas
          Detalles | Menú | Dirección (solo dirección) y pie fijo con los PDF */}
      {showModal && selectedEvent && (() => {
        const ev = selectedEvent;
        const conf = !isPendingEvent;
        // Anterior y Siguiente (solo si ese día hay más de un evento)
        const nav = navEventosRef.current && navEventosRef.current.lista.length > 1 ? navEventosRef.current : null;
        const pestanas = [
          { id: 'detalles', label: 'Detalles', tour: 'evento-tab-detalles' },
          ...(conf ? [{ id: 'menu', label: 'Menú', tour: 'evento-tab-menu' }] : []),
          ...(esDireccionEfectiva ? [{ id: 'direccion', label: 'Dirección', tour: 'evento-tab-direccion' }] : []),
        ];
        // Ordenador: ventana ancha con Detalles a la izquierda y el resto de
        // pestañas a la derecha. Sin pestañas para la derecha, una columna.
        const pestanasDerecha = pestanas.filter(t => t.id !== 'detalles');
        const dosColumnas = !isMobile && pestanasDerecha.length > 0;
        const pestanaDerecha = pestanasDerecha.some(t => t.id === activeTab) ? activeTab : (pestanasDerecha[0]?.id || null);
        const tarjeta = 'bg-white border border-[#E1E6E3] rounded-xl p-3';
        const etiqueta = 'flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#5B6B64] mb-1';
        const fechaLarga = ev.fecha_evento ? (() => {
          const f = fechaDeISO(String(ev.fecha_evento).split('T')[0]);
          return tituloDia(f);
        })() : '';
        // Contenido de Detalles y de Menú (el mismo en móvil y en ordenador)
        const contenidoDetalles = (
          <div className="space-y-3">
            {!conf && (
              <div className="bg-white border border-dashed border-[#6B7872] rounded-xl p-3 text-sm text-[#3B4A44]">
                Presupuesto sin confirmar: todavía no hay orden de cocina ni hoja de material.
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className={tarjeta}>
                <div className={etiqueta}><Clock className="w-3.5 h-3.5" /> Horario</div>
                <div className="font-semibold text-[#1F2A26]">{ev.hora_inicio || '—'} a {ev.hora_fin || '—'}</div>
              </div>
              <div className={tarjeta}>
                <div className={etiqueta}><Users className="w-3.5 h-3.5" /> Personas</div>
                <div className="font-semibold text-[#1F2A26]">{ev.num_personas ? `${ev.num_personas} pax` : '—'}</div>
              </div>
            </div>

            <div className={tarjeta}>
              <div className={etiqueta}><MapPin className="w-3.5 h-3.5" /> Lugar</div>
              <div className="font-semibold text-[#1F2A26]">{ev.lugar_evento || 'Sin indicar'}</div>
              {ev.direccion_exacta && ev.direccion_exacta !== ev.lugar_evento && (
                <div className="text-xs text-[#5B6B64] mt-0.5">{ev.direccion_exacta}</div>
              )}
              {urlIrAlEvento(ev) && (
                <a
                  href={urlIrAlEvento(ev)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center justify-center gap-1.5 w-full sm:w-auto h-11 px-4 text-sm font-semibold text-white rounded-lg hover:opacity-90"
                  style={{ backgroundColor: '#157A58' }}
                  data-testid="calendario-ir-al-evento"
                  data-tour="evento-ir"
                >
                  <MapPin className="w-4 h-4" /> Ir al evento
                </a>
              )}
            </div>

            {ev.contacto_evento && (
              <div className={tarjeta}>
                <div className={etiqueta}><Phone className="w-3.5 h-3.5" /> Contacto</div>
                <div className="font-semibold text-[#1F2A26]">{ev.contacto_evento.nombre}</div>
                {ev.contacto_evento.telefono && (
                  <a href={`tel:${ev.contacto_evento.telefono}`} className="text-sm font-semibold text-[#157A58]" data-tour="evento-telefono">
                    {ev.contacto_evento.telefono}
                  </a>
                )}
              </div>
            )}

            {ev.observaciones && (
              <div className="bg-[#FDF6E7] border border-[#F2DDB0] rounded-xl p-3">
                <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#92400E] mb-1">
                  <AlertTriangle className="w-3.5 h-3.5" /> Observaciones
                </div>
                <div className="text-sm text-[#5C3A0B] whitespace-pre-line">{ev.observaciones}</div>
              </div>
            )}

          </div>
        );
        const contenidoMenu = (
          <div className="space-y-3">
            {ev.menus?.length > 0 ? (
              ev.menus.map((menu, idx) => (
                <div key={`${menu.menu_id || menu.nombre}-${idx}`} className={tarjeta}>
                  <div className="flex justify-between items-start gap-2 mb-2">
                    <h4 className="font-semibold text-[#1F2A26]">{menu.nombre}</h4>
                    {menu.num_personas && (
                      <span className="flex-shrink-0 text-[11px] font-bold px-2 py-0.5 rounded-full bg-[#EEF1EF] text-[#3B4A44]">
                        {menu.num_personas} pax
                      </span>
                    )}
                  </div>
                  {menu.platos?.length > 0 ? (
                    <ul className="space-y-1">
                      {menu.platos.map((plato, pIdx) => (
                        <li key={`${plato.id || plato.nombre}-${pIdx}`} className="text-sm text-[#33443C] pl-3 relative">
                          <span className="absolute left-0 top-2 w-1 h-1 rounded-full bg-[#157A58]" aria-hidden="true" />
                          {plato.nombre}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-[#5B6B64]">Sin platos configurados</p>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-sm text-[#5B6B64]">
                <Package className="w-10 h-10 mx-auto mb-2 text-[#C9D1CD]" />
                {loadingEvent ? 'Cargando menús…' : 'No hay menús configurados'}
              </div>
            )}
          </div>
        );
        return (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
            <div className="absolute inset-0 bg-black/50" onClick={() => setShowModal(false)} />
            <div className={`relative bg-[#F7F9F8] w-full sm:mx-4 rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col ${
              isMobile ? 'sm:max-w-lg max-h-[92dvh]' : (dosColumnas ? 'sm:max-w-4xl max-h-[88dvh]' : 'sm:max-w-lg max-h-[85dvh]')
            }`}>
              {/* Cabecera */}
              <div className="bg-white px-4 pt-4 pb-3 border-b border-[#E1E6E3] flex-shrink-0">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                      <span
                        className="text-[11px] font-bold px-2 py-0.5 rounded-full"
                        style={conf ? { backgroundColor: '#157A58', color: '#FFFFFF' } : { border: '1.5px dashed #6B7872', color: '#3B4A44' }}
                      >
                        {conf ? 'Confirmado' : 'Sin confirmar'}
                      </span>
                      {ev.es_jornada_multi && (
                        <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-[#EFEAFB] text-[#5B3FA6]">
                          Día {ev.jornada_numero} de {ev.total_jornadas}
                        </span>
                      )}
                    </div>
                    <h3 className="text-lg font-bold text-[#1F2A26] leading-snug">
                      {ev.numero ? `${ev.numero} · ` : ''}{ev.cliente?.nombre_empresa || ev.cliente_nombre || 'Cliente'}
                    </h3>
                    <p className="text-sm text-[#5B6B64] mt-0.5">
                      {fechaLarga}
                      {ev.hora_inicio ? ` · ${ev.hora_inicio}${ev.hora_fin ? ` a ${ev.hora_fin}` : ''}` : ''}
                      {ev.num_personas ? ` · ${ev.num_personas} pax` : ''}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowModal(false)}
                    className="w-11 h-11 -mr-2 -mt-1 flex items-center justify-center rounded-lg text-[#3B4A44] hover:bg-[#F4F7F5]"
                    aria-label="Cerrar"
                    data-testid="close-modal-btn"
                    data-tour="evento-cerrar"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                {/* Anterior y Siguiente entre los eventos del mismo día */}
                {nav && (
                  <div className="mt-3 grid grid-cols-[1fr_auto_1fr] items-center gap-2" data-testid="evento-navegacion">
                    <button
                      type="button"
                      onClick={() => irAEventoVecino(-1)}
                      disabled={loadingEvent || nav.indice <= 0}
                      className="h-11 sm:h-9 w-full px-2 rounded-lg border border-[#E1E6E3] bg-[#F4F7F5] text-sm font-semibold text-[#1F2A26] flex items-center justify-center gap-1 hover:bg-[#E9EEEB] disabled:opacity-40 disabled:hover:bg-[#F4F7F5] disabled:cursor-default"
                      aria-label="Evento anterior"
                      title={isMobile ? undefined : 'Evento anterior del día (flecha izquierda)'}
                      data-uso="calendario.evento_anterior"
                      data-testid="evento-anterior"
                    >
                      <ChevronLeft className="w-4 h-4 flex-shrink-0" />
                      Anterior
                    </button>
                    <span className="text-xs font-medium text-[#5B6B64] text-center whitespace-nowrap" aria-live="polite">
                      {nav.indice + 1} de {nav.lista.length} este día
                    </span>
                    <button
                      type="button"
                      onClick={() => irAEventoVecino(1)}
                      disabled={loadingEvent || nav.indice >= nav.lista.length - 1}
                      className="h-11 sm:h-9 w-full px-2 rounded-lg border border-[#E1E6E3] bg-[#F4F7F5] text-sm font-semibold text-[#1F2A26] flex items-center justify-center gap-1 hover:bg-[#E9EEEB] disabled:opacity-40 disabled:hover:bg-[#F4F7F5] disabled:cursor-default"
                      aria-label="Evento siguiente"
                      title={isMobile ? undefined : 'Evento siguiente del día (flecha derecha)'}
                      data-uso="calendario.evento_siguiente"
                      data-guia="evento-siguiente"
                      data-testid="evento-siguiente"
                    >
                      Siguiente
                      <ChevronRight className="w-4 h-4 flex-shrink-0" />
                    </button>
                  </div>
                )}
              </div>

              {dosColumnas ? (
                /* Ordenador: Detalles siempre a la izquierda; Menú y Dirección a la derecha */
                <div className="flex-1 min-h-0 grid grid-cols-[2fr_3fr] grid-rows-[minmax(0,1fr)]" data-testid="evento-dos-columnas">
                  <div className="min-h-0 overflow-y-auto p-4 border-r border-[#E1E6E3]">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-[#5B6B64] mb-2" data-tour="evento-tab-detalles">
                      Detalles
                    </div>
                    {contenidoDetalles}
                  </div>
                  <div className="min-h-0 flex flex-col">
                    <div className="bg-white flex gap-5 px-4 border-b border-[#E1E6E3] flex-shrink-0">
                      {pestanasDerecha.map(tab => (
                        <button
                          key={tab.id}
                          onClick={() => setActiveTab(tab.id)}
                          data-uso="calendario.pestana"
                          data-uso-detalle={tab.id}
                          data-tour={tab.tour}
                          className={`h-11 text-sm border-b-2 transition ${
                            pestanaDerecha === tab.id
                              ? 'border-[#157A58] text-[#1F2A26] font-bold'
                              : 'border-transparent text-[#5B6B64] font-semibold hover:text-[#1F2A26]'
                          }`}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto p-4">
                      {pestanaDerecha === 'menu' && conf && contenidoMenu}
                      {pestanaDerecha === 'direccion' && esDireccionEfectiva && renderDireccionEvento(ev)}
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {/* Pestañas */}
                  {pestanas.length > 1 && (
                    <div className="bg-white flex gap-5 px-4 border-b border-[#E1E6E3] flex-shrink-0">
                      {pestanas.map(tab => (
                        <button
                          key={tab.id}
                          onClick={() => setActiveTab(tab.id)}
                          data-uso="calendario.pestana"
                          data-uso-detalle={tab.id}
                          data-tour={tab.tour}
                          className={`h-11 text-sm border-b-2 transition ${
                            activeTab === tab.id
                              ? 'border-[#157A58] text-[#1F2A26] font-bold'
                              : 'border-transparent text-[#5B6B64] font-semibold hover:text-[#1F2A26]'
                          }`}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Contenido */}
                  <div className="flex-1 overflow-y-auto p-4">
                    {activeTab === 'detalles' && contenidoDetalles}
                    {activeTab === 'menu' && conf && contenidoMenu}
                    {activeTab === 'direccion' && esDireccionEfectiva && renderDireccionEvento(ev)}
                  </div>
                </>
              )}

              {/* Pie fijo: los PDF del evento (si está confirmado) y, para dirección, Abrir el presupuesto */}
              {(conf || puedeVerPresupuestos) && (
                <div
                  className={`bg-white border-t border-[#E1E6E3] p-3 flex-shrink-0 grid gap-2 ${
                    conf ? (puedeVerPresupuestos ? 'grid-cols-4' : 'grid-cols-3') : 'grid-cols-1'
                  }`}
                  data-testid="calendario-pie-pdf"
                >
                  {conf && (<>
                  <button
                    onClick={handleDownloadOrdenCocina}
                    data-uso="calendario.pdf"
                    data-uso-detalle="orden_cocina"
                    data-tour="evento-pdf-orden"
                    disabled={downloadingPdf}
                    className="h-12 px-1 rounded-lg text-xs sm:text-sm font-semibold text-white flex items-center justify-center gap-1.5 hover:opacity-90 disabled:opacity-50"
                    style={{ backgroundColor: '#157A58' }}
                  >
                    {downloadingPdf ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    Orden cocina
                  </button>
                  <button
                    onClick={handleDownloadHojaMaterial}
                    data-uso="calendario.pdf"
                    data-uso-detalle="hoja_material"
                    data-tour="evento-pdf-material"
                    disabled={downloadingPdf}
                    className="h-12 px-1 rounded-lg border border-[#E1E6E3] bg-white text-xs sm:text-sm font-semibold text-[#1F2A26] flex items-center justify-center gap-1.5 hover:bg-[#F4F7F5] disabled:opacity-50"
                  >
                    <Package className="w-4 h-4" />
                    Hoja material
                  </button>
                  <button
                    onClick={handleOpenMinutaModal}
                    data-uso="calendario.pdf"
                    data-uso-detalle="minuta_abrir"
                    disabled={downloadingPdf}
                    className="h-12 px-1 rounded-lg border border-[#E1E6E3] bg-white text-xs sm:text-sm font-semibold text-[#1F2A26] flex items-center justify-center gap-1.5 hover:bg-[#F4F7F5] disabled:opacity-50"
                  >
                    <FileText className="w-4 h-4" />
                    Minuta
                  </button>
                  </>)}
                  {puedeVerPresupuestos && (
                    <button
                      onClick={() => {
                        setShowModal(false);
                        navigate(`/presupuestos/${ev.presupuesto_id || ev.id}`);
                      }}
                      data-testid="go-to-budget-btn"
                      data-uso="calendario.abrir_presupuesto"
                      className="h-12 px-1 rounded-lg border border-[#E1E6E3] bg-white text-xs sm:text-sm font-semibold text-[#1F2A26] flex items-center justify-center gap-1.5 hover:bg-[#F4F7F5]"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Presupuesto
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Modal para ver todos los eventos de un día */}
      {dayEventsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDayEventsModal(null)} />
          <div className="relative bg-white w-full max-w-md mx-4 rounded-xl shadow-2xl overflow-hidden">
            <div className="bg-blue-600 text-white p-4 flex justify-between items-center">
              <h3 className="font-bold">
                Eventos del {dayEventsModal.date.getDate()} de {monthNames[dayEventsModal.date.getMonth()]}
              </h3>
              <button onClick={() => setDayEventsModal(null)} className="hover:bg-blue-700 rounded p-2.5 -m-1.5" aria-label="Cerrar">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 max-h-[60dvh] overflow-y-auto space-y-2">
              {(dayEventsModal.ausencias || []).length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  {(dayEventsModal.ausencias || []).map(a => (
                    <p
                      key={`modal-aus-${a.solicitud_id}-${a.fecha}`}
                      className={`text-xs ${esDiaOcupado(a) ? 'font-semibold rounded px-1.5 py-0.5 my-0.5' : 'text-amber-900'}`}
                      style={esDiaOcupado(a) ? ESTILO_DIA_OCUPADO : undefined}
                    >
                      {textoAusenciaLargo(a)}
                    </p>
                  ))}
                </div>
              )}
              {dayEventsModal.events.map(event => (
                <button
                  key={event.id + (event.jornada_id || '')}
                  data-uso="calendario.abrir"
                  data-uso-detalle="dia"
                  onClick={() => {
                    setDayEventsModal(null);
                    abrirEventoDesde(event);
                  }}
                  className="w-full text-left p-3 bg-gray-50 hover:bg-blue-50 rounded-lg border border-gray-200 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {event.cliente_nombre || 'Cliente'}
                      </p>
                      <p className="text-sm text-gray-500">
                        Presupuesto #{event.numero}
                      </p>
                    </div>
                    <div className="text-right ml-2">
                      <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                        <Users size={12} className="mr-1" />
                        {event.num_personas}p
                      </span>
                    </div>
                  </div>
                  {event.lugar_evento && (
                    <p className="text-xs text-gray-400 mt-1 truncate flex items-center">
                      <MapPin size={10} className="mr-1 flex-shrink-0" />
                      {event.lugar_evento}
                    </p>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Configuración de Minuta */}
      {showMinutaModal && selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-2 sm:p-4">
          <div className={`bg-white rounded-xl shadow-xl w-full flex flex-col ${
            isMobile ? 'max-h-[95dvh] max-w-full' : 'max-w-lg max-h-[85dvh]'
          }`}>
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-3 rounded-t-xl flex-shrink-0">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="font-bold text-lg">Configurar Minuta</h3>
                  <p className="text-amber-100 text-xs mt-0.5">
                    Presupuesto #{selectedEvent.numero}
                  </p>
                </div>
                <button
                  onClick={() => setShowMinutaModal(false)}
                  className="text-white/80 hover:text-white p-2.5 -m-1.5"
                  aria-label="Cerrar"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Contenido */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Toggle de idioma */}
              <div className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-gray-900 flex items-center gap-2 text-sm">
                      <Globe className="w-4 h-4" /> Añadir inglés
                    </h4>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {minutaConfig.mostrarIngles 
                        ? 'Español + inglés (bilingüe)' 
                        : 'Solo español'}
                    </p>
                  </div>
                  <button
                    onClick={() => setMinutaConfig({
                      ...minutaConfig,
                      mostrarIngles: !minutaConfig.mostrarIngles
                    })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                      minutaConfig.mostrarIngles ? 'bg-green-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        minutaConfig.mostrarIngles ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                {minutaConfig.mostrarIngles && (
                  <div className="mt-2 text-xs text-blue-700 bg-blue-100 px-2 py-1 rounded inline-block">
                    ✓ Español + English
                  </div>
                )}
              </div>

              {/* Selección de Menús */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-2 text-sm">Seleccionar Menús</h4>
                {(selectedEvent.menus || selectedEvent.menus_personalizados || []).length > 0 ? (
                  <div className="space-y-2">
                    {(selectedEvent.menus || selectedEvent.menus_personalizados || []).map((menu, menuIdx) => {
                      const menuId = menu.menu_id || `menu-${menuIdx}`;
                      const isSelected = minutaConfig.menusSeleccionados.includes(menuId);
                      const platosCount = menu.platos?.length || menu.platos_ids?.length || 0;
                      
                      return (
                        <div 
                          key={menuIdx} 
                          className={`border rounded-lg p-3 transition-colors ${
                            isSelected ? 'border-amber-400 bg-amber-50' : 'border-gray-200 bg-gray-50'
                          }`}
                        >
                          <label className="flex items-start gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setMinutaConfig({
                                    ...minutaConfig,
                                    menusSeleccionados: [...minutaConfig.menusSeleccionados, menuId]
                                  });
                                } else {
                                  setMinutaConfig({
                                    ...minutaConfig,
                                    menusSeleccionados: minutaConfig.menusSeleccionados.filter(id => id !== menuId)
                                  });
                                }
                              }}
                              className="mt-0.5 w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-900 text-sm truncate">
                                {menu.nombre_menu || menu.nombre || 'Menú sin nombre'}
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                {menu.num_personas || selectedEvent.num_personas || 0} personas • {platosCount} platos
                              </div>
                            </div>
                          </label>

                          {/* Preview de platos si está seleccionado */}
                          {isSelected && menu.platos && menu.platos.length > 0 && (
                            <div className="mt-2 ml-7 pt-2 border-t border-gray-200">
                              <div className="text-xs text-gray-500 space-y-1">
                                {menu.platos.slice(0, 5).map((plato, pIdx) => (
                                  <div key={pIdx} className="flex items-center gap-1.5">
                                    <div className="w-1 h-1 bg-amber-500 rounded-full" />
                                    <span className="truncate">{plato.nombre}</span>
                                  </div>
                                ))}
                                {menu.platos.length > 5 && (
                                  <div className="text-gray-400 italic">
                                    +{menu.platos.length - 5} platos más
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-6 bg-gray-50 rounded-lg">
                    <FileText className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                    <p className="text-gray-500 text-sm">No hay menús configurados</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Añade menús al presupuesto para generar minutas
                    </p>
                  </div>
                )}
              </div>

              {/* Resumen */}
              {minutaConfig.menusSeleccionados.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-green-700 text-sm font-medium">
                    <span>✓</span>
                    <span>{minutaConfig.menusSeleccionados.length} menú(s) seleccionado(s)</span>
                  </div>
                </div>
              )}
            </div>

            {/* Footer con botones */}
            <div className="flex-shrink-0 p-4 border-t bg-gray-50 rounded-b-xl">
              <div className={`flex gap-2 ${isMobile ? 'flex-col' : 'justify-end'}`}>
                <button
                  onClick={() => setShowMinutaModal(false)}
                  className={`px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-100 transition ${
                    isMobile ? 'w-full order-2' : ''
                  }`}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleGenerarMinuta}
                  data-uso="calendario.pdf"
                  data-uso-detalle="minuta"
                  disabled={generatingMinuta || minutaConfig.menusSeleccionados.length === 0}
                  className={`px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-medium hover:from-amber-600 hover:to-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-2 ${
                    isMobile ? 'w-full order-1' : ''
                  }`}
                >
                  {generatingMinuta ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      Generando...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      Descargar Minuta
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <Toaster />
    </div>
  );
};

export default CalendarioEventos;
